import type { Locale } from './config'

/* eslint-disable @typescript-eslint/no-explicit-any */

const dictionaries = {
  fr: {
    // ─── Navigation ───
    nav: {
      impact: 'Impact',
      projects: 'Projets',
      carte: 'Carte',
      companies: 'Entreprises',
      howItWorks: 'Comment ça marche',
      pricing: 'Tarifs',
      blog: 'Blog',
      about: 'À propos',
      contact: 'Nous contacter',
      switchLang: 'EN',
      switchLangLabel: 'Passer en anglais',
      contributionCarbone: 'Contribution carbone',
      menu: 'Menu',
      login: 'Se connecter',
      myAccount: 'Mon espace',
      contributorSpace: 'Espace contributeur',
    },
    // ─── Home page ───
    home: {
      badge: 'Certifié B Corp • Label Bas-Carbone',
      titlePart1: 'Pour une',
      titleHighlight: 'contribution carbone',
      titlePart2: 'à impact écosystémique',
      subtitle:
        "Stock CO2 accompagne les entreprises engagées avec des projets français labellisés Label Bas-Carbone. Un impact mesuré sur 5 dimensions écosystémiques : climat, biodiversité, eau, sols et socio-économique.",
      ctaPrimary: 'Échanger avec un expert',
      ctaSecondary: 'Découvrir nos projets →',
      trustLabel: 'Label Bas-Carbone',
      trustBCorp: 'Certifié B Corp',
      trustFrance: '100% projets français',
      trustDimensions: "5 dimensions d'impact",
    },
    // ─── Stats (libellés seuls : les valeurs viennent du catalogue, src/lib/site/chiffres-cles.ts) ───
    stats: {
      tco2: 'tCO₂ de réductions attendues',
      projects: 'Projets labellisés',
      hectares: 'Hectares engagés',
      trees: 'Plants prévus aux dossiers',
      co2Commercialisable: 'tCO₂ commercialisables',
    },
    // ─── Differentiators ───
    diff: {
      badge: 'Pourquoi Stock CO2',
      title: 'La contribution carbone, autrement',
      subtitle:
        'Six engagements qui guident l\'accompagnement de votre stratégie RSE.',
      items: [
        {
          icon: 'label',
          title: "Label d'État français",
          description:
            'Tous nos projets sont labellisés Label Bas-Carbone par le Ministère de la Transition Écologique, garantissant rigueur et transparence.',
        },
        {
          icon: 'leaf',
          title: 'Tous types de projet',
          description:
            'Boisement, reboisement, grandes cultures, carbon agri, vergers, haies, bâtiment — une diversité de méthodes pour maximiser votre impact.',
        },
        {
          icon: 'chart',
          title: 'Cobénéfices vérifiés',
          description:
            "Chaque projet est évalué sur 5 dimensions d'impact : climat, biodiversité, eau, sols et socio-économique, avec des scores audités.",
        },
        {
          icon: 'handshake',
          title: 'Sans intermédiaire',
          description:
            'Relation directe entre votre entreprise et les porteurs de projet. Aucun intermédiaire, aucun surcoût, une traçabilité totale.',
        },
        {
          icon: 'search',
          title: 'Transparence totale',
          description:
            'Accès complet aux données de chaque projet : localisation, essences, surface, métriques carbone, scores de cobénéfices.',
        },
        {
          icon: 'lock',
          title: 'Transaction sécurisée',
          description:
            'Chaque contribution carbone est sécurisée, tracée et vérifiable. Votre investissement RSE est protégé et valorisable.',
        },
      ],
    },
    // ─── Business case ───
    businessCase: {
      badge: 'Le business case',
      title: 'Pourquoi investir dans la contribution carbone ?',
      subtitle: 'Des arguments concrets et fondés sur des données publiques pour justifier votre stratégie carbone.',
      items: [
        {
          icon: 'clipboard',
          title: 'Conformité CSRD',
          description:
            "D'ici 2026, 50 000 entreprises européennes seront soumises au reporting CSRD. La contribution carbone fait partie des attentes des régulateurs.",
          source: 'Source : Commission européenne',
        },
        {
          icon: 'shoppingBag',
          title: 'Image de marque',
          description:
            '78% des consommateurs préfèrent acheter auprès d\'entreprises engagées pour l\'environnement. Un atout commercial direct.',
          source: 'Source : Étude Nielsen, 2023',
        },
        {
          icon: 'users',
          title: 'Attractivité RH',
          description:
            '70% des salariés considèrent l\'engagement RSE comme un critère important dans le choix de leur employeur.',
          source: 'Source : Deloitte, 2024',
        },
        {
          icon: 'trendingUp',
          title: 'Avantage concurrentiel',
          description:
            'Les entreprises à forte performance ESG surperforment de 4,7% en bourse sur le long terme.',
          source: 'Source : MSCI, 2023',
        },
      ],
    },
    // ─── Impact preview ───
    impact: {
      badge: 'Notre impact',
      title: '5 dimensions pour un impact écosystémique complet',
      subtitle: 'Au-delà du carbone, chaque projet génère des cobénéfices mesurés et vérifiés.',
      dimensions: {
        climat: 'Climat',
        biodiversite: 'Biodiversité',
        eau: 'Eau',
        sols: 'Sols',
        socioEconomique: 'Socio-économique',
      },
      explore: 'Explorer notre impact en détail →',
    },
    // ─── Projects preview (home) ───
    projectsPreview: {
      badge: 'Nos projets',
      title: 'Des projets carbone partout en France',
      subtitle: 'Découvrez nos projets forestiers et agricoles labellisés Label Bas-Carbone.',
      viewAll: 'Voir tous nos projets →',
    },
    // ─── Project catalogue (/projets) ───
    projets: {
      badge: 'Catalogue',
      title: 'Nos projets carbone',
      subtitle:
        'Projets forestiers et agricoles labellisés Label Bas-Carbone, partout en France.',
      searchPlaceholder: 'Rechercher par commune, département, nom…',
      allRegions: 'Toutes les régions',
      allMethods: 'Toutes les méthodes',
      filterByMethod: 'Méthode',
      filterByRegion: 'Région',
      found: 'projet(s)',
      noResults: 'Aucun projet trouvé',
      noResultsHint: 'Essayez de modifier vos filtres.',
      resetFilters: 'Réinitialiser les filtres',
      surface: 'Surface',
      totalCO2: 'Total CO₂',
      co2Available: 'tCO₂ disponibles',
      pagination: {
        previous: '← Précédent',
        next: 'Suivant →',
        page: 'Page',
        of: 'sur',
      },
      card: {
        commune: 'Commune',
        departement: 'Département',
        region: 'Région',
        method: 'Méthode',
        surface: 'Surface',
        trees: 'Plants prévus',
        co2: 'CO₂',
        essences: 'Essences',
        view: 'Voir le projet →',
      },
      map: {
        title: 'Répartition géographique',
        subtitle: 'Cliquez sur un département pour voir les projets locaux',
        clickDept: 'Cliquez sur un département',
        projectsInDept: 'projet(s) dans ce département',
        closePanel: 'Fermer',
        source: 'Source : centroïdes des préfectures (IGN / OpenStreetMap)',
      },
    },
    // ─── Project detail (/projets/[slug]) ───
    projetDetail: {
      backToList: '← Retour aux projets',
      notFound: 'Projet non trouvé',
      notFoundHint: 'Ce projet n’existe pas ou a été retiré du catalogue.',
      overview: 'Vue d’ensemble',
      carbonData: 'Données carbone',
      carbonDataTitle: 'Données carbone',
      co2Total: 'CO₂ total séquestré',
      co2TotalShort: 'CO₂ séquestré (total)',
      co2Commercialisable: 'Dont commercialisable',
      co2Available: 'CO₂ disponible',
      surface: 'Surface',
      surfaceHa: 'Surface (ha)',
      trees: 'Plants prévus au dossier',
      treesCount: 'Plants prévus au dossier',
      density: 'Densité',
      densityUnit: 'arbres / ha',
      co2PerHa: 'Ratio CO₂ / ha',
      co2PerHaUnit: 't/ha',
      location: 'Localisation',
      commune: 'Commune',
      departement: 'Département',
      region: 'Région',
      postalCode: 'Code postal',
      method: 'Méthode Label Bas-Carbone',
      methodDescription: 'Description de la méthode',
      methodSource: 'Source : méthode officielle du Label Bas-Carbone — ministère de la Transition écologique.',
      methodDuration: 'Durée',
      species: 'Essences plantées',
      speciesIcon: 'leaf',
      status: 'Statut',
      statusTitle: 'Statut du projet',
      statusLabeled: 'Labellisé',
      statusCertified: 'Labellisé · vérification réalisée',
      labelLabeled: 'Label Bas-Carbone · Labellisé',
      labelCertified: 'Label Bas-Carbone · vérification réalisée',
      projectNumber: 'Référence',
      contactCta: 'Contribuer à ce projet',
      contactCtaHint: 'Nos experts vous recontactent sous 48 h',
      aboutLabelTitle: 'À propos du Label Bas-Carbone',
      aboutLabelText:
        "Label de compensation carbone reconnu par l'État français. Chaque projet est audité par un organisme indépendant et enregistré au registre public.",
      // `labellise` : seul un projet labellisé (ou vérifié) peut se dire labellisé ;
      // un projet notifié ou en montage est seulement engagé dans la démarche.
      heroDescription: (methode: string, surface: string, commune: string | null, co2: string, labellise: boolean) =>
        `Au cœur ${commune ? `de ${commune}, un` : `du territoire, un`} projet de ${methode.toLowerCase()} s'étend sur ${surface} hectares. ${labellise ? "Labellisé Label Bas-Carbone par l'État français" : 'Engagé dans le Label Bas-Carbone'}, il contribue à la séquestration de ${co2} tonnes de CO₂ tout en favorisant la biodiversité locale, la qualité des sols et la résilience des écosystèmes. Un investissement concret pour le climat et les territoires.`,
      ctaBadge: 'Intéressé par ce projet ?',
      ctaTitle: 'Échangez avec un expert Stock CO2',
      ctaSubtitle:
        'Nous vous accompagnons dans le choix du projet qui correspond le mieux à votre stratégie RSE.',
      ctaPrimary: 'Prendre rendez-vous',
      ctaSecondary: 'Voir tous les projets',
      similarProjects: 'Projets similaires',
    },
    // ─── Type labels (lowercase keys matching data/real-projects.json) ───
    typeLabels: {
      boisement: 'Boisement',
      reboisement: 'Reboisement',
      grandes_cultures: 'Grandes cultures',
      carbon_agri: 'Carbon Agri',
      vergers: 'Vergers',
      reduction_intrants: "Réduction d'intrants",
      belle_foret: 'Gestion Forestière à Stock Continu',
      haies: 'Haies',
      batiment: 'Bâtiment',
      elevage: 'Élevage',
    },
    // ─── Method descriptions (official Label Bas-Carbone) ───
    // Durées : pages des méthodes sur label-bas-carbone.ecologie.gouv.fr, contrôlées
    // le 08/10/2026. `null` : la méthode ne fixe pas de durée de projet.
    methods: {
      BOISEMENT: {
        name: 'Boisement',
        description:
          "Plantation d'un massif forestier sur une terre non boisée depuis au moins 10 ans. Le projet séquestre du carbone dans la biomasse (tronc, branches, racines), les sols et les produits bois durables.",
        duration: '30 ans',
      },
      REBOISEMENT: {
        name: 'Reboisement',
        description:
          "Reconstitution d'une forêt détruite par une catastrophe naturelle ou dégradée par un dépérissement. La méthode vise à restaurer des peuplements résilients, adaptés au changement climatique.",
        duration: '30 ans',
      },
      'GRANDES CULTURES': {
        name: 'Grandes cultures',
        description:
          "Transition vers des pratiques agricoles bas-carbone : couverts végétaux, réduction du travail du sol, légumineuses, optimisation de la fertilisation azotée. La méthode valorise l'ensemble des leviers disponibles à l'échelle de l'exploitation.",
        duration: '5 ans',
      },
      CARBON_AGRI: {
        name: 'Carbon Agri',
        description:
          "Élevages bovins et systèmes de polyculture-élevage : réduction des émissions de méthane entérique, stockage de carbone dans les prairies et les haies, optimisation de la gestion des effluents.",
        duration: '5 ans',
      },
      VERGERS: {
        name: 'Vergers',
        description:
          "Plantation et conduite agroécologique de vergers : séquestration dans la biomasse ligneuse, couverts herbacés inter-rangs, réduction des intrants.",
        duration: '20 ans',
      },
      "REDUCTION D'INTRANTS": {
        name: "Réduction d'intrants",
        description:
          "Diminution des engrais de synthèse et ajustement fin de la fertilisation. Réduit directement les émissions de protoxyde d'azote (N₂O), un puissant gaz à effet de serre.",
        duration: '5 ans',
      },
      'LA BELLE FORÊT': {
        name: 'Gestion Forestière à Stock Continu',
        description:
          "Gestion forestière dynamique visant à améliorer la productivité et la résilience des peuplements existants. Cumule séquestration supplémentaire et amélioration de la qualité des bois.",
        duration: '20 ans', // « 20 ans ; vérification … tous les 5 ans » (page de la méthode, mise à jour 29/06/2026)
      },
      HAIES: {
        name: 'Haies',
        description:
          "Plantation et entretien durable de haies bocagères. Outre la séquestration, les haies créent des corridors écologiques, protègent les sols contre l'érosion et régulent l'eau.",
        duration: '15 ans', // « 5 ans renouvelable 2 fois (soit 15 ans) » (méthode Haies du 08/06/2021, § 2.1)
      },
      'BÂTIMENT': {
        name: 'Bâtiment biosourcé',
        description:
          'Construction utilisant des matériaux biosourcés (bois, paille, chanvre) qui stockent durablement du carbone dans la structure du bâtiment.',
        // La méthode Bâtiment (BBCA, décision du 29/03/2023, § 2.3) ne fixe pas de durée
        // de projet : la durée de référence est la durée de vie des matériaux (100 ans au plus).
        duration: null,
      },
    },
    // ─── Carte page ───
    carte: {
      badge: 'Carte interactive',
      title: 'Nos projets carbone en France',
      subtitle:
        'Cartographie des projets Stock CO2 clusterisés par département. Cliquez sur un point pour voir les projets locaux.',
      filters: {
        title: 'Filtrer la carte',
        allMethods: 'Toutes les méthodes',
        allRegions: 'Toutes les régions',
      },
      legend: {
        title: 'Légende',
        clusterHint:
          'La taille du point est proportionnelle au nombre de projets dans le département.',
      },
      source: 'Source des coordonnées : préfectures IGN / OpenStreetMap.',
      map: {
        activeProjects: 'Projets actifs',
        inDepartments: (n: number) => `dans ${n} département${n > 1 ? 's' : ''}`,
        scale: 'Échelle',
        department: 'Département',
        close: 'Fermer',
        projectsLabel: (n: number) => `projet${n > 1 ? 's' : ''}`,
        otherProjects: (n: number) => `+ ${n} autres projets`,
        clickHint: 'Cliquez un cercle pour explorer les projets',
        sizeHint: 'La taille reflète le nombre de projets dans le département',
        legendMarker: 'Projet Label Bas-Carbone',
      },
    },
    // ─── Testimonials ───
    testimonials: {
      badge: 'Témoignages',
      title: 'Ils nous font confiance',
      subtitle: 'Des entreprises et institutions engagées partagent leur expérience.',
    },
    // ─── CTA ───
    cta: {
      badge: "Passez à l'action",
      title: 'Prêt à financer des projets carbone en France ?',
      subtitle: 'Rejoignez les entreprises qui ont choisi Stock CO2 pour leurs projets bas-carbone.',
      primary: 'Échanger avec un expert',
      secondary: 'Découvrir nos projets',
    },
    // ─── Resources page ───
    ressources: {
      badge: 'Ressources',
      title: 'Centre de ressources',
      subtitle:
        'Guides pratiques, outils et contenus pour comprendre la contribution carbone et le Label Bas-Carbone',
      filterAll: 'Tous',
      filterGuides: 'Guides',
      filterTools: 'Outils',
      filterInfographics: 'Infographies',
      filterRegulations: 'Réglementation',
      consultBtn: 'Consulter',
      downloadBtn: 'Télécharger',
      comingSoon: 'Bientôt disponible',
      newsletterTitle: 'Recevez nos analyses et guides chaque mois',
      newsletterPlaceholder: 'Votre email',
      newsletterBtn: 'S\'inscrire',
      ctaBadge: 'Besoin d\'un accompagnement personnalisé ?',
      ctaTitle: 'Échangez avec nos experts',
      resources: [
        {
          id: 'guides-index',
          category: 'guide',
          icon: 'book',
          title: 'Tous nos guides',
          description: 'La liste complète des guides Stock CO2, avec leur date de mise à jour.',
          href: '/ressources/guides',
        },
        {
          id: 'guide-label-lbc',
          category: 'guide',
          icon: 'book',
          title: 'Guide : Comprendre le Label Bas-Carbone',
          description:
            'Tout savoir sur le label officiel français de réduction d\'émissions : fonctionnement, méthodes, acteurs, processus de certification.',
          href: '/ressources/guides/comprendre-label-bas-carbone',
        },
        {
          id: 'guide-premiere-contribution',
          category: 'guide',
          icon: 'book',
          title: 'Guide : Première contribution carbone',
          description:
            'Les étapes pour lancer votre première démarche de contribution carbone en entreprise : diagnostic, choix des projets, budget, communication.',
          href: '/ressources/guides/premiere-contribution-carbone',
        },
        {
          id: 'guide-contribution-vs-compensation',
          category: 'guide',
          icon: 'book',
          title: 'Guide : Contribution vs Compensation',
          description:
            'Compensation carbone et contribution carbone : ce que recouvre chaque terme, ce que la loi encadre et comment communiquer sans risque.',
          href: '/ressources/guides/contribution-vs-compensation',
        },
        {
          id: 'guide-choisir-projets',
          category: 'guide',
          icon: 'book',
          title: 'Guide : Choisir ses projets carbone',
          description:
            'Critères de sélection, différences entre forestier et agricole, questions à poser, pièges à éviter.',
          href: '/ressources/guides/choisir-projets-carbone',
        },
        {
          id: 'guide-marche-lbc',
          category: 'guide',
          icon: 'book',
          title: "Guide : Le march\u00e9 du Label Bas-Carbone",
          description:
            "Chiffres cl\u00e9s 2025 du march\u00e9 carbone volontaire fran\u00e7ais : 2 104 projets, 8,18 MtCO2, r\u00e9partition r\u00e9gionale, op\u00e9rateurs et \u00e9volutions r\u00e9glementaires.",
          href: '/ressources/guides/marche-label-bas-carbone',
        },
        {
          id: 'tool-pricing',
          category: 'tool',
          icon: 'wrench',
          title: 'Simulateur de prix',
          description: "Estimez le co\u00fbt de votre contribution carbone selon le type de projet et le volume.",
          href: '/pricing#simulateur',
        },
        {
          id: 'tool-calculator',
          category: 'tool',
          icon: 'wrench',
          title: 'Calculateur d\'empreinte simplifié',
          description:
            'Estimez rapidement les émissions de votre entreprise pour dimensionner votre contribution.',
          href: '#',
          badge: 'Bientôt disponible',
        },
        {
          id: 'tool-checklist',
          category: 'tool',
          icon: 'wrench',
          title: 'Checklist RSE',
          description: 'Les 10 étapes pour intégrer la contribution carbone dans votre stratégie RSE.',
          href: '#',
          badge: 'À télécharger',
        },
        {
          id: 'infographic-5-dimensions',
          category: 'infographic',
          icon: 'chart',
          title: 'Les 5 dimensions de l\'impact',
          description:
            'Infographie visuelle expliquant comment Stock CO2 mesure l\'impact au-delà du CO₂.',
          href: '/ressources/infographies/5-dimensions-impact',
        },
        {
          id: 'infographic-lifecycle',
          category: 'infographic',
          icon: 'chart',
          title: 'Le parcours d\'un crédit carbone',
          description:
            'De la plantation à votre certificat : le cycle de vie d\'un crédit Label Bas-Carbone.',
          href: '/ressources/infographies/parcours-credit-carbone',
        },
        {
          id: 'infographic-map',
          category: 'infographic',
          icon: 'chart',
          title: 'Carte des projets en France',
          description:
            'Visualisez sur la carte de France les projets Label Bas-Carbone accompagnés par Stock CO2.',
          href: '/carte',
        },
        {
          id: 'regulation-csrd',
          category: 'regulation',
          icon: 'balance',
          title: 'CSRD : ce que ça change pour vous',
          description:
            'Guide pratique sur les obligations de reporting durabilité et comment y répondre.',
          href: '/reporting-csrd',
        },
        {
          id: 'regulation-allegations',
          category: 'regulation',
          icon: 'balance',
          title: 'Allégations environnementales : que dit la loi ?',
          description:
            'Depuis 2023, les termes \'neutre en carbone\' et \'zéro émission\' sont réglementés. Ce qu\'il faut savoir.',
          href: '/ressources/reglementation/allegations-environnementales',
        },
        {
          id: 'regulation-decree-2025',
          category: 'regulation',
          icon: 'balance',
          title: 'Label Bas-Carbone : décret 2025',
          description:
            'Les évolutions réglementaires du Label Bas-Carbone suite au décret de septembre 2025.',
          href: '/blog',
        },
      ],
    },
    // ─── Contact page ───
    contact: {
      badge: 'Contact',
      title: 'Échangez avec nos experts',
      subtitle:
        'Décrivez votre projet et nous vous recontactons sous 24h avec une proposition adaptée.',
      name: 'Nom complet',
      namePlaceholder: 'Jean Dupont',
      email: 'Email professionnel',
      emailPlaceholder: 'jean@entreprise.com',
      phone: 'Téléphone',
      phonePlaceholder: '+33 6 12 34 56 78',
      company: 'Entreprise',
      companyPlaceholder: 'Nom de votre entreprise',
      sector: "Secteur d'activité",
      employees: "Taille de l'entreprise",
      subject: 'Objet',
      subjectPlaceholder: 'Quel est l’objet de votre demande ?',
      tonnes: 'Estimation du tonnage CO₂ souhaité',
      tonnesPlaceholder: 'Ex : 500 tonnes',
      // Champs spécifiques devis
      devisProjectTypes: 'Types de projets souhaités',
      devisProjectOptions: [
        { value: 'forestier-boisement', label: 'Forestier (boisement)' },
        { value: 'forestier-reboisement', label: 'Forestier (reboisement)' },
        { value: 'agricole', label: 'Agricole' },
        { value: 'indifferent', label: 'Indifférent' },
      ],
      devisRegion: 'Région préférée',
      devisRegions: [
        'Auvergne-Rhône-Alpes', 'Bourgogne-Franche-Comté', 'Bretagne',
        'Centre-Val de Loire', 'Corse', 'Grand Est', 'Hauts-de-France',
        'Île-de-France', 'Normandie', 'Nouvelle-Aquitaine', 'Occitanie',
        'Pays de la Loire', "Provence-Alpes-Côte d'Azur", 'Pas de préférence',
      ],
      devisBudget: 'Budget annuel estimé',
      devisBudgetOptions: [
        { value: '<5k', label: '< 5 000 €' },
        { value: '5-20k', label: '5 000 – 20 000 €' },
        { value: '20-50k', label: '20 000 – 50 000 €' },
        { value: '50-100k', label: '50 000 – 100 000 €' },
        { value: '>100k', label: '> 100 000 €' },
      ],
      devisTimeline: 'Délai souhaité',
      devisTimelineOptions: [
        { value: 'immediat', label: 'Immédiat' },
        { value: '1-3mois', label: '1 – 3 mois' },
        { value: '3-6mois', label: '3 – 6 mois' },
        { value: '+6mois', label: '+ 6 mois' },
      ],
      message: 'Message',
      messagePlaceholder: 'Décrivez votre projet ou votre demande...',
      consentPart1: "J'accepte que mes données soient traitées par Stock CO2 conformément à la ",
      consentPart2: '.',
      privacyLink: 'politique de confidentialité',
      submit: 'Envoyer ma demande',
      thanks: 'Merci pour votre message !',
      thanksDetail: 'Notre équipe vous recontactera sous 24h pour discuter de votre projet.',
      select: 'Sélectionnez',
      required: '*',
      sectors: [
        'Industrie',
        'Services / Conseil',
        'Énergie',
        'Transport / Logistique',
        'BTP / Construction',
        'Agriculture / Agroalimentaire',
        'Finance / Assurance',
        'Technologie / IT',
        'Collectivité / Public',
        'Autre',
      ],
      subjects: [
        { value: 'contribution', label: 'Contribuer à un projet carbone' },
        { value: 'information', label: "Obtenir plus d'informations" },
        { value: 'devis', label: 'Demander un devis' },
        { value: 'demonstration', label: "Démonstration de l'espace contributeur" },
        { value: 'partenariat', label: 'Proposer un partenariat' },
        { value: 'presse', label: 'Contact presse' },
        { value: 'reclamation', label: 'Réclamation' },
        { value: 'autre', label: 'Autre sujet' },
      ],
      employeeRanges: [
        { value: '1-10', label: '1 – 10 salariés' },
        { value: '11-50', label: '11 – 50 salariés' },
        { value: '51-250', label: '51 – 250 salariés' },
        { value: '251-1000', label: '251 – 1 000 salariés' },
        { value: '1000+', label: '+ 1 000 salariés' },
      ],
      info: {
        emailTitle: 'Email',
        emailValue: 'contact@stock-co2.fr',
        addressTitle: 'Adresse',
        addressValue: '3 Bd de Sébastopol, 75001 Paris',
        responseTitle: 'Réponse',
        responseValue: 'Sous 24h ouvrées',
      },
    },
    // ─── About page ───
    aPropos: {
      // ─── Raison d'être (14/10/2026, recertification B Corp, exigence B Lab PSG 1.1) ───
      // Texte exact de ~/bcorp-recertification-2027/raison-d-etre_a-propos.md, sections « FR » et
      // « EN » : rien n'est reformulé. La citation reprend l'article 4 des statuts ; ses guillemets
      // et la ponctuation devant « : » sont posés par AboutContent.tsx selon la langue. Le tableau
      // de correspondance B Lab du même fichier est interne et ne va pas sur le site.
      raisonDEtre: {
        titre: "Notre raison d'être",
        citation:
          "Favoriser la réduction des émissions de gaz à effet de serre dans le but de combattre le réchauffement climatique, et générer un impact social, sociétal et environnemental positif et significatif dans l'exercice de nos activités.",
        source: "Inscrite à l'article 4 de nos statuts et approuvée par nos associés.",
        concretTitre: "Ce qu'elle veut dire concrètement",
        concret: [
          { cle: 'Pour le climat', texte: 'financer en France des projets labellisés Label Bas-Carbone (boisements, reboisements, pratiques agricoles bas-carbone, haies, vergers), dont les réductions d\'émissions sont mesurées selon des méthodes approuvées par le ministère de la Transition écologique.' },
          { cle: 'Pour les territoires', texte: "orienter l'argent des entreprises vers les forestiers et les agriculteurs français, pour qu'il paie du travail sur le terrain." },
          { cle: 'Pour la nature', texte: 'choisir et suivre les projets aussi pour ce qu\'ils apportent à la biodiversité, à l\'eau et aux sols, au-delà du carbone.' },
        ],
        activiteTitre: 'Comment elle guide notre activité',
        activiteIntro:
          "Notre métier est de relier les entreprises qui veulent agir pour le climat aux porteurs de projets qui réduisent ou stockent du carbone en France. Notre raison d'être fixe nos règles de conduite\u00a0:",
        regles: [
          "nous tenons la trace de chaque tonne et de chaque euro, jusqu'au projet\u00a0;",
          'nos dirigeants prennent en compte, dans leurs décisions, les conséquences pour nos salariés, nos clients, les porteurs de projets, nos associés et l\'environnement, comme nos statuts le leur imposent.',
        ],
      },
      badge: 'À propos',
      heroTitleP1: 'Pour une',
      heroTitleHighlight: 'contribution carbone',
      heroSubtitle:
        'Stock CO2 accompagne les entreprises et organisations dans leur démarche RSE de compensation carbone avec des projets français labellisés Label Bas-Carbone par le Ministère de la Transition Écologique.',
      title: 'Stock CO2, une contribution carbone ancrée dans les territoires français',
      subtitle:
        'Notre mission : accompagner les entreprises vers une contribution carbone crédible, transparente et ancrée dans les territoires français.',
      missionTitle: 'Notre mission',
      missionHeading: 'Rendre la contribution carbone accessible, transparente et impactante',
      missionText:
        "Stock CO2 est dédié à la lutte contre le changement climatique en soutenant des projets de compensation carbone. Nous collaborons avec des forestiers, des agriculteurs et des institutions pour développer et faire labelliser des projets écologiques selon les critères du Label Bas-Carbone. Notre démarche s'appuie sur la diversité des projets, la sécurisation des crédits carbone, la transparence financière et la qualité des projets mis en œuvre.",
      valuesTitle: 'Nos valeurs',
      valuesSubtitle: 'Ce qui nous guide au quotidien',
      values: [
        { icon: 'search', title: 'Transparence', description: 'Chaque donnée, chaque métrique, chaque euro est tracé et vérifiable. Pas de greenwashing.' },
        { icon: 'leaf', title: 'Impact réel', description: "Nous mesurons l'impact sur 5 dimensions écosystémiques, pas uniquement le carbone." },
        { icon: 'handshake', title: 'Sans intermédiaire', description: "Relation directe entre l'entreprise contributrice et le porteur de projet. Aucun surcoût." },
        { icon: 'chart', title: 'Rigueur scientifique', description: 'Nos méthodes sont validées par le Ministère et auditées par des organismes indépendants.' },
      ],
      historyBadge: 'Notre histoire',
      historyTitle: 'Les grandes étapes',
      milestones: [
        { year: '2019', title: 'Trois amis, une conviction', description: "Thomas Martal, Aubin Labergère et Guillaume Panthou fondent Stock CO2 pour relocaliser la contribution carbone en France. Intégration au studio Urban Odyssey (ICADE) et à l'incubateur HEC à Station F." },
        { year: '2020', title: 'Premiers projets et premiers contributeurs', description: "Labellisation des premiers projets forestiers sous le Label Bas-Carbone. Premiers contributeurs accompagnés et développement d'un calculateur carbone aligné sur les méthodologies LBC forestières." },
        { year: '2021', title: 'Passage à l\'échelle', description: "Accélération significative avec les premiers clients grands comptes (CAC 40). Stock CO2 se développe sur le marché volontaire français." },
        { year: '2022', title: 'Opérateur multi-sectoriel et fiducie', description: "Stock CO2 devient un opérateur carbone multi-sectoriel (forêt, agriculture, élevage). Mise en place d'une fiducie environnementale à la Caisse des Dépôts. Compensation des émissions du Ministère de la Transition Écologique. Premier bilan carbone interne publié." },
        { year: '2023', title: "Raison d'être, AFEF et assurance", description: "La raison d'être est inscrite dans les statuts. Stock CO2 rejoint l'Association Française des Eaux et Forêts (AFEF) et met en place une couverture assurance pour sécuriser ses projets carbone." },
        { year: '2024', title: 'B Corp et reconnaissance institutionnelle', description: "Couverture assurance Groupama. Certification B Corp obtenue. Contribution carbone pour Choose France, l'OFB et le GIEC." },
        { year: '2025', title: 'Nature+ Coalition', description: "Cofondation de la Nature+ Coalition aux côtés de Reforest'Action, SE Advisory Services (ex-Ecoact, Schneider Electric) et Evolution Market pour fédérer les acteurs des solutions fondées sur la nature." },
      ],
      certificationsBadge: 'Labels & Certifications',
      certificationsTitle: 'Des engagements labellisés et certifiés',
      certifications: [
        { icon: 'label', name: 'Label Bas-Carbone', desc: "Label officiel de l'État français" },
        { icon: 'bcorp', name: 'B Corp', desc: 'Certification B Lab internationale' },
      ],
      partnersTitle: 'Notre réseau de partenaires',
      merciBadge: 'Méthode M.E.R.C.I',
      merciTitle: 'Mesurer, Éviter, Réduire, Compenser, Itérer',
      merciSubtitle:
        'Notre approche intégrée pour accompagner les entreprises vers une stratégie carbone ambitieuse et vérifiable.',
      merciCta: 'Découvrir notre impact en détail →',
      ctaBadge: 'Rejoignez-nous',
      ctaTitle: 'Engagez votre entreprise dans la transition',
      ctaSubtitle:
        'Rejoignez les entreprises qui font confiance à Stock CO2 pour leur contribution carbone.',
      ctaPrimary: 'Nous contacter',
      ctaSecondary: 'Voir nos projets',
      teamTitle: 'Une équipe engagée',
      teamText:
        "Forestiers, ingénieurs agronomes, experts RSE et développeurs : notre équipe réunit les compétences nécessaires pour identifier, qualifier et suivre les projets carbone français.",
      visionText:
        "Nous croyons en une contribution carbone positive, ancrée dans les territoires et portée par des projets concrets. Notre ambition : devenir la référence européenne de la contribution carbone locale. La France d'abord, l'Europe ensuite — avec la même exigence de transparence, de rigueur et d'impact réel.",
      originTitle: 'Notre histoire',
      originText:
        "En 2019, Thomas Martal, Aubin Labergère et Guillaume Panthou — trois amis préoccupés par l'urgence climatique — décident de créer Stock CO2. Leur constat : les entreprises françaises manquent d'une offre de contribution carbone locale, crédible et transparente. Plutôt que d'envoyer des fonds à l'autre bout du monde, ils choisissent de relocaliser l'impact en France, en travaillant directement avec des forestiers et des agriculteurs du territoire.",
      bilanTitle: 'Bilan Carbone : indépendance et rigueur',
      bilanText:
        "Stock CO2 est membre de l'Association Bilan Carbone et formé à la réalisation de bilans carbone. Pourtant, depuis 2020, nous avons fait le choix délibéré de ne pas réaliser les bilans carbone de nos contributeurs. Pourquoi ? Pour éviter d'être juge et partie. Un prestataire qui réalise le bilan carbone d'une entreprise puis lui vend des crédits carbone se retrouve dans une situation de conflit d'intérêts : il pourrait être tenté de gonfler les résultats pour justifier un plus grand volume de contribution. Chez Stock CO2, nous préférons séparer clairement les rôles : le diagnostic d'un côté, la contribution de l'autre.",
      afefTitle: 'AFEF — Eaux et Forêts',
      afefText:
        "Stock CO2 est membre de l'Association Française des Eaux et Forêts (AFEF). Aubin Labergère, cofondateur, en est le secrétaire général. Cette implication reflète notre ancrage profond dans les enjeux forestiers et environnementaux français.",
      coalitionTitle: 'Nature+ Coalition',
      coalitionText:
        "En 2025, Stock CO2 cofonde la Nature+ Coalition, une alliance réunissant des acteurs majeurs des solutions fondées sur la nature. Aux côtés de Reforest'Action, SE Advisory Services (ex-Ecoact, Schneider Electric) et Evolution Market, nous œuvrons à structurer et crédibiliser le marché des projets nature en France et en Europe. Guillaume Panthou, cofondateur de Stock CO2, en est le vice-président.",
    },
    // ─── FAQ ───
    faq: {
      badge: 'FAQ',
      title: 'Questions fréquentes',
      subtitle:
        'Retrouvez les réponses à vos questions sur la contribution carbone, nos projets, la tarification et la conformité CSRD.',
      searchPlaceholder: 'Rechercher une question…',
      noResults: 'Aucune question ne correspond à votre recherche.',
      categories: {
        general: 'Général',
        tarifs: 'Tarifs & Contribution',
        csrd: 'CSRD & Reporting',
        projets: 'Proposer un projet',
        label: 'Label Bas-Carbone',
      },
      ctaBadge: 'Encore des questions ?',
      ctaTitle: 'Échangez directement avec notre équipe',
      ctaSubtitle: 'Nous répondons à toutes vos questions sur la contribution carbone et nos projets.',
      ctaPrimary: 'Nous contacter',
      sections: [
        {
          title: 'Général',
          items: [
            {
              q: "Qu'est-ce que la contribution carbone ?",
              a: "La contribution carbone est une action permettant de compenser les émissions de gaz à effet de serre en finançant des projets qui réduisent ou capturent du CO₂. Chez Stock CO2, tous les projets sont labellisés Label Bas-Carbone par le Ministère de la Transition Écologique.",
            },
            {
              q: 'Quelle est la différence entre compensation et contribution carbone ?',
              a: "La contribution carbone est une approche plus responsable que la simple compensation. Elle s'inscrit dans une démarche globale : mesurer, éviter, réduire puis contribuer. Stock CO2 accompagne cette démarche complète via la méthode M.E.R.C.I.",
            },
            {
              q: 'Comment vos crédits carbone sont-ils labellisés ?',
              a: "Tous nos projets sont labellisés Label Bas-Carbone, le label officiel de l'État français. Chaque projet suit un processus rigoureux d'audit et de suivi quinquennal par des auditeurs indépendants.",
            },
            {
              q: "À qui s'adresse Stock CO2 ?",
              a: "Stock CO2 s'adresse aux entreprises et organisations souhaitant s'engager dans une démarche de contribution carbone responsable et vérifiable, dans le cadre de leur stratégie RSE.",
            },
            {
              q: 'Quels types de projets proposez-vous ?',
              a: "Nous proposons des projets de reboisement, boisement, grandes cultures (Carbon Agri), élevage et bâtiment. Tous sont situés en France et labellisés Label Bas-Carbone.",
            },
            {
              q: 'Que sont les cobénéfices ?',
              a: "Les cobénéfices sont les impacts positifs supplémentaires d'un projet carbone au-delà de la séquestration de CO₂ : biodiversité, préservation de l'eau, protection des sols, impacts socio-économiques.",
            },
          ],
        },
        {
          title: 'Tarifs & Contribution',
          items: [
            {
              q: 'Pourquoi le prix varie-t-il entre 30€ et 50€ ?',
              a: 'Le prix dépend du type de projet (forestier vs agricole), de la localisation géographique, des co-bénéfices mesurés (biodiversité, eau, sols, impact socio-économique), et des conditions spécifiques du terrain. Les projets agricoles peuvent atteindre 50€/tCO₂ grâce aux importants co-bénéfices agronomiques.',
            },
            {
              q: "Que comprend le prix d'un crédit carbone ?",
              a: 'Le prix inclut : (1) les coûts de mise en œuvre du projet (35%), (2) les assurances contre les risques (15%), (3) les frais de fiducie qui garantissent la pérennité jusqu’à 9 ans pour la forêt et 5 ans pour l’agriculture (20%), (4) les frais opérateur de Stock CO2 pour gestion et commercialisation (20%), (5) les frais de certification Label Bas-Carbone par audits indépendants (10%). Aucun coût caché.',
            },
            {
              q: 'Pourquoi les prix augmentent-ils chaque année ?',
              a: "Les prix augmentent de 5-10% par an depuis 2019 en raison de : l'inflation des matériaux (plants, équipements), de l'augmentation de la main-d'œuvre, et de l'accroissement des normes environnementales et d'assurance. Cette tendance reflète les coûts réels d'implémentation. Stock CO2 ajuste ses tarifs annuellement pour maintenir la qualité des projets.",
            },
            {
              q: 'Le Label Bas-Carbone est-il moins cher que Verra ou Gold Standard ?',
              a: "Oui, le Label Bas-Carbone offre une tarification 10-20% plus compétitive que les standards internationaux. Pourquoi ? Parce qu'il n'y a pas d'intermédiaires non mandatés. Stock CO2 opère directement, ce qui réduit les frais de commercialisation. Vous bénéficiez d'une plus grande transparence et d'une meilleure valeur pour votre investissement carbone.",
            },
            {
              q: 'Comment est calculé le prix d\'un crédit carbone ?',
              a: 'Stock CO2 utilise un modèle de coûts complets qui intègre : les dépenses de terrain (mise en œuvre, assurances, fiducie), les frais opérationnels, et les frais de certification. Chaque projet est audité indépendamment. Les prix sont définis annuellement en fonction des coûts réels, de la demande du marché, et des objectifs climatiques de la France.',
            },
            {
              q: 'Y a-t-il des frais cachés ?',
              a: 'Non. Notre tarification est complètement transparente. Le prix affiché inclut tous les coûts : mise en œuvre, assurances, fiducie, exploitation, certification. Aucun surcoût surprise. Vous payez exactement ce qui est annoncé, avec une traçabilité complète de l\'utilisation de votre contribution.',
            },
            {
              q: 'Peut-on avoir un tarif dégressif pour de gros volumes ?',
              a: 'Oui. Pour les acquisitions de 1000+ tCO₂, nous proposons des tarifs dégressifs et des conditions d\'accompagnement personnalisées. Contactez notre équipe pour discuter de votre projet et obtenir un devis adapté à votre volumétrie.',
            },
            {
              q: 'Quel est le délai pour recevoir mes crédits carbones ?',
              a: 'Une fois votre commande confirmée, la sélection et l\'intégration de vos projets prennent 2-4 semaines. Les crédits sont générés selon le calendrier d\'émission du Label Bas-Carbone. Pour les projets forestiers, le délai moyen est de 6 mois. Pour les projets agricoles, de 3-4 mois.',
            },
            {
              q: 'Comment fonctionne le processus de contribution ?',
              a: "Le processus est simple : nous échangeons sur vos besoins, vous choisissez un ou plusieurs projets, nous établissons un contrat de contribution et vous recevez une attestation de contribution carbone Label Bas-Carbone.",
            },
            {
              q: 'Puis-je valoriser ma contribution dans mon reporting RSE ?',
              a: 'Oui, votre contribution carbone Label Bas-Carbone est valorisable dans votre reporting RSE, bilan carbone et communication extra-financière. Nous vous fournissons tous les documents nécessaires.',
            },
          ],
        },
        {
          title: 'CSRD & Reporting',
          items: [
            {
              q: 'Les crédits carbone peuvent-ils compenser mes émissions dans le reporting CSRD ?',
              a: 'Non. La CSRD exige un reporting brut (sans compensation). Les crédits carbone achetés doivent être déclarés séparément en ESRS E1-7 : montant en tCO₂, standard de certification (Label Bas-Carbone), et % réduction vs. séquestration. Stock CO2 fournit ces données pour un reporting compliant.',
            },
            {
              q: 'Comment déclarer ma contribution carbone dans mon rapport CSRD ?',
              a: 'Via la section ESRS E1-7 de votre déclaration. Vous déclarez : les tonnes CO₂ finançées, le standard de certification (Label Bas-Carbone), la répartition réduction/séquestration. Stock CO2 structure ces données pour faciliter votre reporting, et fournit une traçabilité complète d\'audit.',
            },
            {
              q: 'Le Label Bas-Carbone est-il reconnu pour la CSRD ?',
              a: 'Oui. Le Label Bas-Carbone est un standard français porté par le Ministère de la Transition Écologique. Il est explicitement reconnu dans les normes ESRS comme un standard de qualité pour la déclaration des crédits carbone achetés.',
            },
            {
              q: 'Stock CO2 remplace-t-il un bilan carbone ?',
              a: 'Non, Stock CO2 est complémentaire. Un bilan carbone mesure vos émissions (étape 1 : identifier et réduire). Stock CO2 finance des projets de séquestration pour vos émissions résiduelles (étape 2 : contribuer). Les deux sont essentiels pour la CSRD : d\'abord mesurer, puis agir.',
            },
            {
              q: 'Qu\'est-ce que la double matérialité (CSRD) ?',
              a: 'La double matérialité évalue deux directions : (1) l\'impact de l\'entreprise sur l\'environnement (ex. vos émissions carbone), et (2) l\'impact de l\'environnement sur l\'entreprise (ex. risques climatiques physiques). Les projets Label Bas-Carbone couvrent les deux, justifiant votre engagement CSRD.',
            },
            {
              q: 'Quand mon entreprise doit-elle se conformer à la CSRD ?',
              a: '2025 : Grandes entreprises soumises à la NFRD (>500 salariés). 2026 : Autres grandes entreprises (1000+ salariés, CA >450M€). 2027-2028 : PME cotées (avec option de report de 2 ans). Stock CO2 vous aide dès aujourd\'hui à préparer votre reporting.',
            },
            {
              q: 'Un seul projet Label Bas-Carbone peut-il couvrir plusieurs normes ESRS ?',
              a: 'Oui. Chaque projet Label Bas-Carbone est évalué sur 5 dimensions de co-bénéfices : climat (E1), biodiversité (E4), eau (E3), sols (E5), et impact socio-économique (S3). Un projet peut donc apporter des contributions auprès de plusieurs normes ESRS simultanément.',
            },
            {
              q: 'Quelles sont les sanctions en cas de non-conformité CSRD ?',
              a: 'Les entreprises non-conformes font face à des sanctions réglementaires, des amendes administratives, et une atteinte à leur réputation (perte de confiance des investisseurs, clients, et partenaires). La conformité CSRD devient progressivement obligatoire. Stock CO2 vous aide à l\'anticiper.',
            },
          ],
        },
        {
          title: 'Proposer un projet',
          items: [
            {
              q: 'Qui peut bénéficier de la certification Label Bas-Carbone ?',
              a: 'Tout propriétaire ou gestionnaire d\'une parcelle forestière ou agricole peut proposer un projet. La faisabilité sera évaluée lors du diagnostic : taille minimale, potentiel carbone, localisation, et conformité aux critères de durabilité.',
            },
            {
              q: 'Quel est le coût pour proposer et faire labelliser mon projet ?',
              a: 'Stock CO2 prend une commission sur les crédits carbone commercialisés. Il n\'y a pas de coût initial pour proposer votre projet ou conduire le diagnostic. Les frais de certification sont partagés équitablement entre Stock CO2 et le porteur de projet.',
            },
            {
              q: 'Combien de temps faut-il pour obtenir la certification Label Bas-Carbone ?',
              a: 'Généralement 6 à 12 mois selon la complexité du projet et les ajustements éventuels du dossier. Le diagnostic prend 2-4 semaines, et le montage dossier 3-6 mois.',
            },
            {
              q: 'Quelles obligations dois-je respecter une fois labellisé ?',
              a: 'Vous devez maintenir les pratiques décrites dans votre plan de gestion durant toute la durée du projet (généralement 30-50 ans pour les projets forestiers). Stock CO2 assure le suivi et le reporting annuel pour garantir la conformité.',
            },
          ],
        },
        {
          title: 'Label Bas-Carbone',
          items: [
            {
              q: "Qu'est-ce que le Label Bas-Carbone ?",
              a: "Le Label Bas-Carbone est le label officiel de l'État français, créé en 2019 par le Ministère de la Transition Écologique. Il labellise les projets de réduction et séquestration des émissions de GES sur le territoire français.",
            },
            {
              q: "Qu'est-ce que la certification B Corp ?",
              a: "B Corp est une certification internationale délivrée par B Lab qui atteste que Stock CO2 répond aux normes les plus exigeantes en matière d'impact social et environnemental, de gouvernance et de transparence.",
            },
            {
              q: 'Comment choisir le bon projet pour mon entreprise ?',
              a: "Nos experts vous accompagnent dans le choix du projet le plus adapté à votre secteur d'activité, votre localisation et vos objectifs RSE.",
            },
          ],
        },
      ],
    },
    // ─── Entreprises page ───
    entreprises: {
      badge: 'Entreprises',
      title: 'Une démarche carbone crédible pour votre entreprise',
      subtitle:
        'Des projets français labellisés, un reporting clair et un accompagnement sur toute la chaîne de valeur.',
      offerTitle: 'Notre offre entreprises',
      stepsTitle: 'Comment ça marche ?',
      steps: [
        { num: '1', title: 'Diagnostic', text: 'Analyse de votre empreinte et de vos objectifs RSE.' },
        { num: '2', title: 'Sélection de projets', text: 'Choix de projets adaptés à votre secteur et vos valeurs.' },
        { num: '3', title: 'Contribution', text: 'Transaction sécurisée et reporting détaillé.' },
        { num: '4', title: 'Valorisation', text: 'Communication RSE et reporting CSRD-compatible.' },
      ],
      platformTitle: 'Votre espace de suivi',
      platformSubtitle: 'Suivez vos contributions en temps réel avec une plateforme dédiée aux entreprises engagées.',
      platformDescription: 'Découvrez l\'espace de suivi et ses fonctionnalités avec nos équipes.',
      platformFeatures: [
        {
          icon: 'clock',
          title: 'Suivi en temps réel',
          description: 'Visualisez l\'état de vos projets et crédits carbone en un coup d\'œil. Tableaux de bord personnalisés et mises à jour instantanées.',
        },
        {
          icon: 'document',
          title: 'Reporting automatisé',
          description: 'Exports CSRD-compatibles pour votre reporting ESG. Documents prêts à intégrer dans votre rapport de durabilité.',
        },
        {
          icon: 'chart',
          title: 'Tableau de bord',
          description: 'Métriques d\'impact sur les 5 dimensions écosystémiques. Visualisez votre contribution au climat, à la biodiversité, à l\'eau, aux sols et au socio-économique.',
        },
      ],
      platformCta: 'Demander une présentation',
    },
    // ─── Forestiers page ───
    forestiers: {
      badge: 'Projets forestiers',
      title: 'Reboisement et boisement labellisés',
      subtitle:
        'Nos projets forestiers restaurent les écosystèmes, séquestrent du carbone et renforcent la biodiversité locale.',
      intro:
        "Stock CO2 accompagne des projets de reboisement et de boisement labellisés Label Bas-Carbone à travers toute la France. Chaque projet est sélectionné pour son potentiel de séquestration carbone et ses cobénéfices sur la biodiversité, l'eau, les sols et l'économie locale.",
      statsLabels: {
        co2: 'tCO₂ stockées (forestier)',
        projects: 'Projets forestiers',
        hectares: 'Hectares concernés',
        trees: 'Plants prévus aux dossiers',
      },
      featuresBadge: 'Nos atouts',
      featuresTitle: 'Ce qui fait la différence',
      features: [
        {
          icon: 'pine',
          title: 'Essences diversifiées',
          description:
            "Plantation d'essences variées (chêne, tilleul, charme, merisier…) pour renforcer la résilience des forêts face au changement climatique.",
        },
        {
          icon: 'butterfly',
          title: 'Corridors écologiques',
          description:
            'Création de corridors reliant les massifs forestiers pour favoriser la circulation de la faune et la pollinisation.',
        },
        {
          icon: 'droplets',
          title: 'Protection des eaux',
          description:
            "Les forêts filtrent naturellement l'eau et protègent les nappes phréatiques. Nos projets sont souvent situés dans des zones de captage.",
        },
        {
          icon: 'clipboard',
          title: 'Suivi quinquennal',
          description:
            "Chaque projet fait l'objet d'un suivi tous les 5 ans par des auditeurs indépendants pour vérifier la séquestration effective.",
        },
      ],
      seeAllProjects: 'Voir tous les projets →',
      cta: 'Proposer un projet forestier',
    },
    // ─── Agricoles page ───
    agricoles: {
      badge: 'Projets agricoles',
      title: 'Agriculture bas-carbone labellisée',
      subtitle:
        'Nos projets agricoles réduisent les émissions, enrichissent les sols et soutiennent les agriculteurs français.',
      intro:
        "Stock CO2 accompagne des projets agricoles bas-carbone via la méthode Carbon Agri, labellisés Label Bas-Carbone. Ces projets optimisent les pratiques culturales et d'élevage pour réduire les émissions de gaz à effet de serre tout en améliorant la santé des sols.",
      statsLabels: {
        co2: 'tCO₂ réduites (agricole)',
        projects: 'Projets agricoles',
        hectares: 'Hectares concernés',
        regions: 'Régions couvertes',
      },
      featuresBadge: 'Nos atouts',
      featuresTitle: 'Ce qui fait la différence',
      features: [
        {
          icon: 'wheat',
          title: 'Pratiques régénératives',
          description:
            'Couverture permanente des sols, rotation des cultures, réduction des intrants chimiques pour restaurer la fertilité naturelle.',
        },
        {
          icon: 'cow',
          title: 'Élevage bas-carbone',
          description:
            "Optimisation de l'alimentation animale, gestion des effluents et valorisation du pâturage pour réduire les émissions.",
        },
        {
          icon: 'sprout',
          title: 'Séquestration dans les sols',
          description:
            'Les pratiques agricoles régénératives augmentent le carbone organique des sols, créant un puits de carbone durable.',
        },
        {
          icon: 'handshake',
          title: 'Soutien aux agriculteurs',
          description:
            "Les crédits carbone génèrent un revenu complémentaire pour les agriculteurs qui s'engagent dans la transition.",
        },
      ],
      seeAllProjects: 'Voir tous les projets →',
      cta: 'Proposer un projet agricole',
    },
    // ─── Sectoral CTA (shared) ───
    sectoralCta: {
      badge: "Passez à l'action",
      title: 'Contribuez à la transition avec nos projets',
      subtitle: 'Choisissez le projet qui correspond à votre stratégie RSE et à vos valeurs.',
      primary: 'Échanger avec un expert',
      secondary: 'Découvrir nos projets',
    },
    // ─── Companies (entreprises) listing page ───
    entreprisesPage: {
      badge: 'Entreprises',
      title: 'Ils contribuent avec Stock CO2',
      subtitle: 'Découvrez les entreprises et organisations qui nous font confiance.',
      sectorLabel: 'Secteur',
      seeMore: 'Voir le partenariat →',
      backToList: '← Toutes les entreprises',
      engagementTitle: 'Leur engagement',
      ctaBadge: 'Inspiré ?',
      ctaTitle: 'Engagez votre entreprise comme eux',
      ctaSubtitle: 'Rejoignez les entreprises qui soutiennent des projets de compensation carbone avec Stock CO2.',
      ctaPrimary: 'Nous contacter',
      notFound: 'Entreprise non trouvée',
    },
    // ─── Impact page (detailed) ───
    impactPage: {
      badge: 'Impact',
      title: 'Notre méthode d’évaluation en 5 dimensions',
      subtitle:
        'Au-delà du carbone, nous évaluons chaque projet sur des cobénéfices audités et vérifiés.',
      heroBadge: 'Notre impact',
      heroTitleP1: 'Un impact mesuré sur',
      heroTitleHighlight: '5 dimensions',
      heroTitleP2: 'écosystémiques',
      heroSubtitle:
        "Au-delà du carbone, chaque projet Stock CO2 génère des cobénéfices vérifiés sur l'ensemble de l'écosystème : climat, biodiversité, eau, sols, socio-économique.",
      statsLabels: {
        projects: 'Projets labellisés',
        co2: 'tCO₂ séquestrées',
        hectares: 'Hectares concernés',
        regions: 'Régions françaises',
      },
      dimensionsBadge: '5 dimensions',
      dimensionsTitle: 'Un impact écosystémique complet',
      dimensionsSubtitle:
        'Chaque dimension est qualifiée à partir de la documentation officielle du Label Bas-Carbone et des éléments fournis par les porteurs de projet.',
      dimensions: {
        climat: {
          title: 'Climat',
          description:
            'Quantité de CO₂ séquestré ou évité, mesurée selon les méthodes officielles du Label Bas-Carbone.',
          facts: [
            "Méthodes reconnues par le Ministère de la Transition Écologique",
            "Suivi quinquennal de chaque projet par un auditeur indépendant",
            "Réductions et séquestrations enregistrées dans le registre public Label Bas-Carbone",
          ],
        },
        biodiversite: {
          title: 'Biodiversité',
          description:
            'Diversité des essences, qualité des habitats, présence d’espèces indicatrices et continuité écologique.',
          facts: [
            "Diversification des essences et structures forestières",
            "Création ou maintien d'habitats : haies, lisières, mares",
            "Recommandations issues des méthodes officielles du Label Bas-Carbone",
          ],
        },
        eau: {
          title: 'Eau',
          description:
            'Qualité des eaux, régulation hydrologique, maintien des zones humides et gestion de l’érosion.',
          facts: [
            "Réduction du ruissellement et de l'érosion hydrique",
            "Maintien des zones humides et des sols vivants",
            "Pratiques favorisant l'infiltration et la qualité de l'eau",
          ],
        },
        sols: {
          title: 'Sols',
          description:
            "Stockage du carbone dans les sols, vie biologique, résistance à l'érosion et fertilité durable.",
          facts: [
            "Couverts végétaux et travail du sol réduit",
            "Apports organiques et préservation de la matière organique",
            "Suivi des indicateurs de qualité des sols",
          ],
        },
        socioEconomique: {
          title: 'Socio-économique',
          description:
            "Travail mobilisé sur le terrain, valorisation des territoires ruraux, dimension éducative et transmission.",
          facts: [
            "Relation directe avec les forestiers et agriculteurs porteurs de projet",
            "Activité maintenue dans les filières locales (pépinières, ETF)",
            "Valorisation du patrimoine naturel des territoires",
          ],
        },
      },
      methodologyBadge: 'Méthode M.E.R.C.I',
      methodologyTitle: 'Une méthodologie en 5 étapes',
      methodologyIntro:
        'Mesurer, Éviter, Réduire, Compenser, Itérer — notre approche intégrée pour une stratégie carbone ambitieuse et crédible.',
      methodologySteps: [
        {
          number: 'M',
          title: 'Mesurer',
          icon: 'chart',
          text: "Réaliser un bilan carbone complet de votre organisation selon une méthodologie certifiée. Identifier vos sources d'émissions.",
        },
        {
          number: 'É',
          title: 'Éviter',
          icon: 'ban',
          text: "Mettre en place des actions concrètes pour éviter les émissions à la source. Prioriser la sobriété et l'écoconception.",
        },
        {
          number: 'R',
          title: 'Réduire',
          icon: 'trending-down',
          text: 'Optimiser vos processus pour réduire significativement vos émissions incompressibles. Fixer des objectifs mesurables.',
        },
        {
          number: 'C',
          title: 'Contribuer',
          icon: 'sprout',
          text: 'Compenser vos émissions résiduelles en finançant des projets labellisés Label Bas-Carbone à impact écosystémique.',
        },
        {
          number: 'I',
          title: 'Itérer',
          icon: 'refresh',
          text: 'Suivre, auditer et améliorer en continu votre démarche. Chaque année, progresser vers une empreinte toujours plus réduite.',
        },
      ],
      certificationsBadge: 'Certifications & Labels',
      certificationsTitle: 'Des engagements labellisés et certifiés',
      certificationsSubtitle:
        'Notre démarche est validée par des organismes indépendants et reconnue au niveau national et international.',
      certificationsItems: [
        {
          name: 'Label Bas-Carbone',
          issuer: 'Ministère de la Transition Écologique',
          icon: 'label',
          description:
            "Certification officielle de l'État français garantissant l'additionnalité et la qualité des projets carbone.",
        },
        {
          name: 'B Corp',
          issuer: 'B Lab',
          icon: 'bcorp',
          description:
            "Certification internationale attestant que Stock CO2 répond à des normes exigeantes en matière d'impact social et environnemental.",
        },
      ],
      partnersNetworkTitle: 'Notre réseau de partenaires',
      ctaBadge: "Contribuez à l'impact",
      ctaTitle: 'Mesurez votre empreinte, choisissez votre impact',
      ctaSubtitle:
        'Chaque contribution finance des projets à impact écosystémique mesurable et vérifiable.',
      ctaPrimary: 'Démarrer votre contribution',
      ctaSecondary: 'Voir nos projets',
    },
    // ─── Engagements ───
    engagements: {
      badge: 'Nos engagements',
      title: 'Six engagements pour une contribution carbone crédible',
      subtitle:
        'Stock CO2 s’appuie sur un cadre déontologique strict pour garantir l’impact réel de chaque projet.',
      heroBadge: 'Nos engagements',
      heroTitle: 'Engagés pour le climat',
      heroSubtitle:
        'Nos engagements vont au-delà des mots. Certifications, publications et méthodologie rigoureuse guident notre action quotidienne.',
      items: [
        {
          icon: 'bcorp',
          title: 'Certifié B Corp',
          description:
            "Stock CO2 a obtenu la certification B Corp délivrée par B Lab, attestant que nous répondons à des normes exigeantes en matière d'impact social, environnemental, de gouvernance et de transparence.",
        },
        {
          icon: 'chart',
          title: 'Un bilan carbone publié chaque année',
          description:
            "Nous publions notre propre bilan carbone chaque année depuis l'exercice 2025 (bilans 2023 et 2025 réalisés). Nous mesurons, réduisons et compensons nos émissions résiduelles avec la même rigueur que celle proposée à nos clients.",
        },
        {
          icon: 'refresh',
          title: 'Méthode M.E.R.C.I',
          description:
            'Mesurer, Éviter, Réduire, Contribuer, Itérer — notre méthodologie en 5 étapes guide chaque entreprise vers une stratégie carbone ambitieuse et vérifiable.',
        },
        {
          icon: 'document',
          title: "Raison d'être inscrite aux statuts",
          description:
            "Depuis 2023, notre raison d'être est inscrite dans les statuts de la société, garantissant que notre mission environnementale guide chaque décision stratégique.",
        },
      ],
      positionBadge: 'Position papers',
      positionTitle: 'Nos prises de position',
      positionSubtitle:
        'Retrouvez nos publications et analyses sur les enjeux de la contribution carbone.',
      positionPapers: [
        { title: 'Méthode M.E.R.C.I', theme: 'rse' },
        { title: 'La contribution carbone responsable', theme: 'carbone' },
        { title: 'Biodiversité et projets forestiers', theme: 'biodiversité' },
        { title: 'CSRD et reporting extra-financier', theme: 'réglementation' },
      ],
      readMore: 'Lire →',
      ctaBadge: 'Rejoignez le mouvement',
      ctaTitle: 'Engagez votre entreprise pour le climat',
      ctaPrimary: 'Nous contacter',
      ctaSecondary: 'Découvrir notre impact',
    },
    // ─── Blog ───
    blog: {
      heroBadge: 'Blog',
      heroTitle: 'Actualités carbone et RSE',
      heroSubtitle:
        "Analyses, guides pratiques et retours d'expérience pour éclairer votre démarche de contribution carbone.",
      categoryAll: 'Tous',
      categories: {
        carbone: 'Carbone',
        biodiversite: 'Biodiversité',
        reglementation: 'Réglementation',
        rse: 'RSE',
        projets: 'Projets',
        innovation: 'Innovation',
        actualites: 'Actualités',
        guides: 'Guides pratiques',
        temoignages: 'Témoignages',
        impact: 'Impact & Résultats',
        dossiers: 'Dossiers Experts',
      },
      readTimeUnit: 'min',
      backToBlog: '← Retour au blog',
      allArticles: '← Tous les articles',
      notFound: 'Article non trouvé',
      ctaBadge: "Envie d'en savoir plus ?",
      ctaTitle: 'Échangez avec nos experts carbone',
      ctaSubtitle:
        "Nous vous accompagnons dans la définition et la mise en œuvre de votre stratégie de contribution carbone.",
      ctaPrimary: 'Nous contacter',
    },
    // ─── Footer ───
    footer: {
      tagline: 'La contribution carbone à impact écosystémique, avec des projets français labellisés.',
      newsletter: 'Restez informé de nos projets',
      newsletterSub:
        'Recevez nos actualités et nouvelles opportunités de contribution carbone.',
      emailPlaceholder: 'Votre email professionnel',
      subscribe: "S'inscrire",
      subscribed: 'Merci, vous êtes inscrit !',
      solutions: 'Solutions',
      solutionsLinks: {
        entreprises: 'Entreprises',
        forestiers: 'Forestiers',
        agricoles: 'Agricoles',
        impact: 'Notre impact',
        pricing: 'Tarifs',
        howItWorks: 'Comment ça marche',
        reportingCsrd: 'Reporting CSRD',
      },
      company: 'Entreprise',
      companyLinks: {
        about: 'À propos',
        engagements: 'Nos engagements',
        caseStudies: 'Cas clients',
        blog: 'Blog',
        contact: 'Contact',
      },
      legal: 'Légal',
      legalLinks: {
        mentions: 'Mentions légales',
        confidentialite: 'Politique de confidentialité',
        cgu: 'CGU',
        cookies: 'Cookies',
      },
      rights: 'Tous droits réservés.',
      certified: 'Certifié B Corp',
      label: 'Label Bas-Carbone',
    },
    // ─── 404 / Not found ───
    notFound: {
      title: 'Page introuvable',
      subtitle:
        "Cette page semble s'être perdue dans la forêt. Pas d'inquiétude, nos projets carbone sont toujours là !",
      backHome: "Retour à l'accueil",
      seeProjects: 'Voir nos projets',
    },
    // ─── Common ───
    common: {
      backTo: '← Retour',
      readMore: 'Lire →',
      learnMore: 'En savoir plus',
      loading: 'Chargement…',
      error: 'Une erreur est survenue.',
      yes: 'Oui',
      no: 'Non',
      close: 'Fermer',
      open: 'Ouvrir',
    },
  },
  en: {
    // ─── Navigation ───
    nav: {
      impact: 'Impact',
      projects: 'Projects',
      carte: 'Map',
      companies: 'Companies',
      howItWorks: 'How it works',
      pricing: 'Pricing',
      blog: 'Blog',
      about: 'About',
      contact: 'Contact us',
      switchLang: 'FR',
      switchLangLabel: 'Switch to French',
      contributionCarbone: 'Carbon contribution',
      menu: 'Menu',
      login: 'Log in',
      myAccount: 'My space',
      contributorSpace: 'Contributor space',
    },
    home: {
      badge: 'B Corp Certified • Label Bas-Carbone',
      titlePart1: 'For a',
      titleHighlight: 'carbon contribution',
      titlePart2: 'with ecosystem impact',
      subtitle:
        'Stock CO2 supports engaged companies with French projects labelled under the Label Bas-Carbone. Impact measured across 5 ecosystem dimensions: climate, biodiversity, water, soil and socio-economic.',
      ctaPrimary: 'Talk to an expert',
      ctaSecondary: 'Discover our projects →',
      trustLabel: 'Label Bas-Carbone',
      trustBCorp: 'B Corp Certified',
      trustFrance: '100% French projects',
      trustDimensions: '5 impact dimensions',
    },
    stats: {
      tco2: 'tCO₂ of expected reductions',
      projects: 'Labelled projects',
      hectares: 'Hectares committed',
      trees: 'Seedlings planned in project files',
      co2Commercialisable: 'Available tCO₂',
    },
    diff: {
      badge: 'Why Stock CO2',
      title: 'Carbon contribution, reimagined',
      subtitle: 'Six commitments that guide how we support your CSR strategy.',
      items: [
        {
          icon: 'label',
          title: 'French state label',
          description:
            'All projects are labelled under the Label Bas-Carbone issued by the French Ministry of Ecological Transition, ensuring rigour and transparency.',
        },
        {
          icon: 'leaf',
          title: 'All project types',
          description:
            'Afforestation, reforestation, large crops, carbon agri, orchards, hedges, buildings — a diverse ecosystem of projects to maximise your environmental impact.',
        },
        {
          icon: 'chart',
          title: 'Verified co-benefits',
          description:
            'Every project is assessed across 5 dimensions: climate, biodiversity, water, soil and socio-economic, with audited scores.',
        },
        {
          icon: 'handshake',
          title: 'No intermediaries',
          description:
            'Direct relationship between your company and project operators. No middleman, no hidden fees, full traceability.',
        },
        {
          icon: 'search',
          title: 'Full transparency',
          description:
            'Full access to each project data: location, species, area, carbon metrics and co-benefit scores.',
        },
        {
          icon: 'lock',
          title: 'Secure transaction',
          description:
            'Every carbon contribution is secured, tracked and verifiable. Your CSR investment is protected and reportable.',
        },
      ],
    },
    businessCase: {
      badge: 'The business case',
      title: 'Why invest in carbon contribution?',
      subtitle: 'Concrete arguments based on public data to justify your carbon strategy.',
      items: [
        {
          icon: 'clipboard',
          title: 'CSRD Compliance',
          description:
            'By 2026, 50,000 European companies will be subject to CSRD reporting. Carbon contribution is part of regulatory expectations.',
          source: 'Source: European Commission',
        },
        {
          icon: 'shoppingBag',
          title: 'Brand image',
          description:
            '78% of consumers prefer to buy from companies committed to the environment. A direct commercial advantage.',
          source: 'Source: Nielsen study, 2023',
        },
        {
          icon: 'users',
          title: 'HR attractiveness',
          description:
            '70% of employees consider CSR commitment an important factor in choosing their employer.',
          source: 'Source: Deloitte, 2024',
        },
        {
          icon: 'trendingUp',
          title: 'Competitive advantage',
          description:
            'Companies with strong ESG performance outperform by 4.7% in stock markets long-term.',
          source: 'Source: MSCI, 2023',
        },
      ],
    },
    impact: {
      badge: 'Our impact',
      title: '5 dimensions for a complete ecosystem impact',
      subtitle: 'Beyond carbon, each project generates measured and verified co-benefits.',
      dimensions: {
        climat: 'Climate',
        biodiversite: 'Biodiversity',
        eau: 'Water',
        sols: 'Soil',
        socioEconomique: 'Socio-economic',
      },
      explore: 'Explore our impact in detail →',
    },
    projectsPreview: {
      badge: 'Our projects',
      title: 'Carbon projects across France',
      subtitle: 'Explore our forestry and agricultural projects labelled under the Label Bas-Carbone.',
      viewAll: 'View all projects →',
    },
    projets: {
      badge: 'Catalogue',
      title: 'Our carbon projects',
      subtitle:
        'Forestry and agricultural projects labelled under the Label Bas-Carbone, all over France.',
      searchPlaceholder: 'Search by town, department, name…',
      allRegions: 'All regions',
      allMethods: 'All methods',
      filterByMethod: 'Method',
      filterByRegion: 'Region',
      found: 'project(s)',
      noResults: 'No project found',
      noResultsHint: 'Try adjusting your filters.',
      resetFilters: 'Reset filters',
      surface: 'Area',
      totalCO2: 'Total CO₂',
      co2Available: 'available tCO₂',
      pagination: {
        previous: '← Previous',
        next: 'Next →',
        page: 'Page',
        of: 'of',
      },
      card: {
        commune: 'Town',
        departement: 'Department',
        region: 'Region',
        method: 'Method',
        surface: 'Area',
        trees: 'Planned seedlings',
        co2: 'CO₂',
        essences: 'Species',
        view: 'View project →',
      },
      map: {
        title: 'Geographic distribution',
        subtitle: 'Click on a department to see local projects',
        clickDept: 'Click on a department',
        projectsInDept: 'project(s) in this department',
        closePanel: 'Close',
        source: 'Source: prefecture centroids (IGN / OpenStreetMap)',
      },
    },
    projetDetail: {
      backToList: '← Back to projects',
      notFound: 'Project not found',
      notFoundHint: 'This project does not exist or has been removed from the catalogue.',
      overview: 'Overview',
      carbonData: 'Carbon data',
      carbonDataTitle: 'Carbon data',
      co2Total: 'Total CO₂ stored',
      co2TotalShort: 'CO₂ stored (total)',
      co2Commercialisable: 'Of which available',
      co2Available: 'Available CO₂',
      surface: 'Area',
      surfaceHa: 'Area (ha)',
      trees: 'Seedlings planned in the project file',
      treesCount: 'Seedlings planned in the project file',
      density: 'Density',
      densityUnit: 'trees / ha',
      co2PerHa: 'CO₂ ratio / ha',
      co2PerHaUnit: 't/ha',
      location: 'Location',
      commune: 'Town',
      departement: 'Department',
      region: 'Region',
      postalCode: 'Postal code',
      method: 'Label Bas-Carbone method',
      methodDescription: 'Method description',
      methodSource: 'Source: official Label Bas-Carbone method — French Ministry for Ecological Transition.',
      methodDuration: 'Duration',
      species: 'Species planted',
      speciesIcon: 'leaf',
      status: 'Status',
      statusTitle: 'Project status',
      statusLabeled: 'Labelled',
      statusCertified: 'Labelled · verification completed',
      labelLabeled: 'Label Bas-Carbone · Labelled',
      labelCertified: 'Label Bas-Carbone · verification completed',
      projectNumber: 'Reference',
      contactCta: 'Support this project',
      contactCtaHint: 'Our experts will get back to you within 48h',
      aboutLabelTitle: 'About the Label Bas-Carbone',
      aboutLabelText:
        'The only carbon contribution label officially recognised by the French state. Every project is audited by an independent body and listed in the public registry.',
      heroDescription: (methode: string, surface: string, commune: string | null, co2: string, labellise: boolean) =>
        `${commune ? `In ${commune}, a` : 'A'} ${methode.toLowerCase()} project spans ${surface} hectares. ${labellise ? "Labelled under France's Label Bas-Carbone" : "Engaged in France's Label Bas-Carbone"}, it contributes to the sequestration of ${co2} tonnes of CO₂ while enhancing local biodiversity, soil health, and ecosystem resilience. A concrete investment for climate and communities.`,
      ctaBadge: 'Interested in this project?',
      ctaTitle: 'Talk to a Stock CO2 expert',
      ctaSubtitle:
        'We help you select the project that best fits your CSR strategy.',
      ctaPrimary: 'Book a meeting',
      ctaSecondary: 'View all projects',
      similarProjects: 'Similar projects',
    },
    typeLabels: {
      boisement: 'Afforestation',
      reboisement: 'Reforestation',
      grandes_cultures: 'Large crops',
      carbon_agri: 'Carbon Agri',
      vergers: 'Orchards',
      reduction_intrants: 'Input reduction',
      belle_foret: 'Continuous Stock Forest Management',
      haies: 'Hedges',
      batiment: 'Building',
      elevage: 'Livestock',
    },
    methods: {
      BOISEMENT: {
        name: 'Afforestation',
        description:
          "Planting a forest on land that has not been wooded for at least 10 years. The project stores carbon in biomass (trunks, branches, roots), soils and durable wood products.",
        duration: '30 years',
      },
      REBOISEMENT: {
        name: 'Reforestation',
        description:
          'Rebuilding a forest destroyed by a natural disaster or degraded by dieback. The method aims to restore resilient stands adapted to climate change.',
        duration: '30 years',
      },
      'GRANDES CULTURES': {
        name: 'Large crops',
        description:
          'Transition to low-carbon farming practices: cover crops, reduced tillage, legumes, fine-tuned nitrogen fertilisation. The method leverages every tool available across the farm.',
        duration: '5 years',
      },
      CARBON_AGRI: {
        name: 'Carbon Agri',
        description:
          'Cattle farms and mixed crop-livestock systems: reducing enteric methane emissions, storing carbon in pastures and hedges, optimising effluent management.',
        duration: '5 years',
      },
      VERGERS: {
        name: 'Orchards',
        description:
          'Planting and agroecological management of orchards: sequestration in woody biomass, inter-row grass cover, input reduction.',
        duration: '20 years',
      },
      "REDUCTION D'INTRANTS": {
        name: 'Input reduction',
        description:
          'Reducing synthetic fertilisers and fine-tuning fertilisation. Directly cuts nitrous oxide (N₂O) emissions, a powerful greenhouse gas.',
        duration: '5 years',
      },
      'LA BELLE FORÊT': {
        name: 'Continuous Stock Forest Management',
        description:
          'Dynamic forest management aimed at improving the productivity and resilience of existing stands. Combines additional sequestration with improved wood quality.',
        duration: '20 years',
      },
      HAIES: {
        name: 'Hedges',
        description:
          'Planting and sustainable upkeep of rural hedges. Beyond sequestration, hedges create ecological corridors, protect soils against erosion and regulate water.',
        duration: '15 years',
      },
      'BÂTIMENT': {
        name: 'Bio-based buildings',
        description:
          'Construction using bio-sourced materials (wood, straw, hemp) that store carbon durably in the building structure.',
        duration: null,
      },
    },
    carte: {
      badge: 'Interactive map',
      title: 'Our carbon projects in France',
      subtitle:
        'Map of Stock CO2 projects clustered by department. Click a marker to see local projects.',
      filters: {
        title: 'Filter the map',
        allMethods: 'All methods',
        allRegions: 'All regions',
      },
      legend: {
        title: 'Legend',
        clusterHint:
          'Marker size is proportional to the number of projects in the department.',
      },
      source: 'Coordinate source: IGN / OpenStreetMap prefecture centroids.',
      map: {
        activeProjects: 'Active projects',
        inDepartments: (n: number) => `across ${n} department${n > 1 ? 's' : ''}`,
        scale: 'Scale',
        department: 'Department',
        close: 'Close',
        projectsLabel: (n: number) => `project${n > 1 ? 's' : ''}`,
        otherProjects: (n: number) => `+ ${n} more projects`,
        clickHint: 'Click a circle to explore the projects',
        sizeHint: 'Size reflects the number of projects in the department',
        legendMarker: 'Label Bas-Carbone project',
      },
    },
    testimonials: {
      badge: 'Testimonials',
      title: 'They trust us',
      subtitle: 'Companies and institutions share their experience.',
    },
    cta: {
      badge: 'Take action',
      title: 'Ready to fund carbon projects in France?',
      subtitle: 'Join the companies that chose Stock CO2 for their low-carbon projects.',
      primary: 'Talk to an expert',
      secondary: 'Discover our projects',
    },
    // ─── Resources page ───
    ressources: {
      badge: 'Resources',
      title: 'Resource Centre',
      subtitle:
        'Practical guides, tools and content to understand carbon contribution and the Label Bas-Carbone',
      filterAll: 'All',
      filterGuides: 'Guides',
      filterTools: 'Tools',
      filterInfographics: 'Infographics',
      filterRegulations: 'Regulations',
      consultBtn: 'Learn more',
      downloadBtn: 'Download',
      comingSoon: 'Coming soon',
      newsletterTitle: 'Get our analyses and guides each month',
      newsletterPlaceholder: 'Your email',
      newsletterBtn: 'Subscribe',
      ctaBadge: 'Need personalized support?',
      ctaTitle: 'Talk to our experts',
      resources: [
        {
          id: 'guides-index',
          category: 'guide',
          icon: 'book',
          title: 'All our guides',
          description: 'The full list of Stock CO2 guides, with their last update date.',
          href: '/ressources/guides',
        },
        {
          id: 'guide-label-lbc',
          category: 'guide',
          icon: 'book',
          title: 'Guide: Understanding Label Bas-Carbone',
          description:
            'Everything you need to know about the official French emission reduction label: how it works, methods, actors, certification process.',
          href: '/en/ressources/guides/comprendre-label-bas-carbone',
        },
        {
          id: 'guide-premiere-contribution',
          category: 'guide',
          icon: 'book',
          title: 'Guide: Your First Carbon Contribution',
          description:
            'Steps to launch your first carbon contribution initiative: diagnostic, project selection, budgeting, communication.',
          href: '/en/ressources/guides/premiere-contribution-carbone',
        },
        {
          id: 'guide-contribution-vs-compensation',
          category: 'guide',
          icon: 'book',
          title: 'Guide: Contribution vs Offsetting',
          description:
            'Carbon offsetting and carbon contribution: what each term covers, what the law regulates and how to communicate safely.',
          href: '/en/ressources/guides/contribution-vs-compensation',
        },
        {
          id: 'guide-choisir-projets',
          category: 'guide',
          icon: 'book',
          title: 'Guide: Choosing Your Carbon Projects',
          description:
            'Selection criteria, differences between forestry and agricultural projects, key questions, pitfalls to avoid.',
          href: '/en/ressources/guides/choisir-projets-carbone',
        },
        {
          id: 'guide-marche-lbc',
          category: 'guide',
          icon: 'book',
          title: 'Guide: The Label Bas-Carbone Market',
          description:
            '2025 key figures for the French voluntary carbon market: 2,104 projects, 8.18 MtCO2, regional breakdown, operators and regulatory changes.',
          href: '/en/ressources/guides/low-carbon-label-market',
        },
        {
          id: 'tool-pricing',
          category: 'tool',
          icon: 'wrench',
          title: 'Pricing Simulator',
          description: 'Estimate your carbon contribution cost based on project type and volume.',
          href: '/en/pricing#simulateur',
        },
        {
          id: 'tool-calculator',
          category: 'tool',
          icon: 'wrench',
          title: 'Simplified Footprint Calculator',
          description:
            'Quickly estimate your company\'s emissions to size your contribution.',
          href: '#',
          badge: 'Coming soon',
        },
        {
          id: 'tool-checklist',
          category: 'tool',
          icon: 'wrench',
          title: 'CSR Checklist',
          description: '10 steps to integrate carbon contribution into your CSR strategy.',
          href: '#',
          badge: 'To download',
        },
        {
          id: 'infographic-5-dimensions',
          category: 'infographic',
          icon: 'chart',
          title: 'The 5 Dimensions of Impact',
          description:
            'Visual infographic explaining how Stock CO2 measures impact beyond CO₂.',
          href: '/en/ressources/infographies/5-dimensions-impact',
        },
        {
          id: 'infographic-lifecycle',
          category: 'infographic',
          icon: 'chart',
          title: 'The Journey of a Carbon Credit',
          description:
            'From tree-planting to your certificate: the lifecycle of a Label Bas-Carbone credit.',
          href: '/en/ressources/infographies/parcours-credit-carbone',
        },
        {
          id: 'infographic-map',
          category: 'infographic',
          icon: 'chart',
          title: 'France Project Map',
          description:
            'See on a map of France the Label Bas-Carbone projects supported by Stock CO2.',
          href: '/en/carte',
        },
        {
          id: 'regulation-csrd',
          category: 'regulation',
          icon: 'balance',
          title: 'CSRD: What Changes for You',
          description:
            'Practical guide on sustainability reporting obligations and how to respond.',
          href: '/en/reporting-csrd',
        },
        {
          id: 'regulation-allegations',
          category: 'regulation',
          icon: 'balance',
          title: 'Environmental Claims: What Does the Law Say?',
          description:
            'Since 2023, terms like "carbon neutral" and "zero emissions" are regulated. What you need to know.',
          href: '/en/ressources/reglementation/allegations-environnementales',
        },
        {
          id: 'regulation-decree-2025',
          category: 'regulation',
          icon: 'balance',
          title: 'Label Bas-Carbone: 2025 Decree',
          description:
            'Regulatory changes to Label Bas-Carbone following the September 2025 decree.',
          href: '/en/blog',
        },
      ],
    },
    contact: {
      badge: 'Contact',
      title: 'Talk to our experts',
      subtitle:
        'Describe your project and we will get back to you within 24h with a tailored proposal.',
      name: 'Full name',
      namePlaceholder: 'John Doe',
      email: 'Professional email',
      emailPlaceholder: 'john@company.com',
      phone: 'Phone',
      phonePlaceholder: '+33 6 12 34 56 78',
      company: 'Company',
      companyPlaceholder: 'Your company name',
      sector: 'Industry',
      employees: 'Company size',
      subject: 'Subject',
      subjectPlaceholder: 'What is your request about?',
      tonnes: 'Estimated CO₂ tonnes needed',
      tonnesPlaceholder: 'e.g. 500 tonnes',
      // Quote-specific fields
      devisProjectTypes: 'Desired project types',
      devisProjectOptions: [
        { value: 'forestier-boisement', label: 'Forest (afforestation)' },
        { value: 'forestier-reboisement', label: 'Forest (reforestation)' },
        { value: 'agricole', label: 'Agricultural' },
        { value: 'indifferent', label: 'No preference' },
      ],
      devisRegion: 'Preferred region',
      devisRegions: [
        'Auvergne-Rhône-Alpes', 'Bourgogne-Franche-Comté', 'Brittany',
        'Centre-Val de Loire', 'Corsica', 'Grand Est', 'Hauts-de-France',
        'Île-de-France', 'Normandy', 'Nouvelle-Aquitaine', 'Occitania',
        'Pays de la Loire', "Provence-Alpes-Côte d'Azur", 'No preference',
      ],
      devisBudget: 'Estimated annual budget',
      devisBudgetOptions: [
        { value: '<5k', label: '< €5,000' },
        { value: '5-20k', label: '€5,000 – €20,000' },
        { value: '20-50k', label: '€20,000 – €50,000' },
        { value: '50-100k', label: '€50,000 – €100,000' },
        { value: '>100k', label: '> €100,000' },
      ],
      devisTimeline: 'Desired timeline',
      devisTimelineOptions: [
        { value: 'immediat', label: 'Immediate' },
        { value: '1-3mois', label: '1 – 3 months' },
        { value: '3-6mois', label: '3 – 6 months' },
        { value: '+6mois', label: '6+ months' },
      ],
      message: 'Message',
      messagePlaceholder: 'Describe your project or request...',
      consentPart1: 'I agree that my data is processed by Stock CO2 according to the ',
      consentPart2: '.',
      privacyLink: 'privacy policy',
      submit: 'Send my request',
      thanks: 'Thank you for your message!',
      thanksDetail: 'Our team will contact you within 24h to discuss your project.',
      select: 'Select',
      required: '*',
      sectors: [
        'Industry',
        'Services / Consulting',
        'Energy',
        'Transport / Logistics',
        'Construction',
        'Agriculture / Food',
        'Finance / Insurance',
        'Technology / IT',
        'Public sector',
        'Other',
      ],
      subjects: [
        { value: 'contribution', label: 'Contribute to a carbon project' },
        { value: 'information', label: 'Get more information' },
        { value: 'devis', label: 'Request a quote' },
        { value: 'demonstration', label: 'Contributor space demonstration' },
        { value: 'partenariat', label: 'Propose a partnership' },
        { value: 'presse', label: 'Press contact' },
        { value: 'reclamation', label: 'Complaint' },
        { value: 'autre', label: 'Other' },
      ],
      employeeRanges: [
        { value: '1-10', label: '1 – 10 employees' },
        { value: '11-50', label: '11 – 50 employees' },
        { value: '51-250', label: '51 – 250 employees' },
        { value: '251-1000', label: '251 – 1,000 employees' },
        { value: '1000+', label: '1,000+ employees' },
      ],
      info: {
        emailTitle: 'Email',
        emailValue: 'contact@stock-co2.fr',
        addressTitle: 'Address',
        addressValue: '3 Bd de Sébastopol, 75001 Paris',
        responseTitle: 'Reply time',
        responseValue: 'Within 24h on business days',
      },
    },
    aPropos: {
      raisonDEtre: {
        titre: 'Our purpose',
        citation:
          'To foster the reduction of greenhouse gas emissions in order to fight global warming, and to generate a positive and significant social, societal and environmental impact through our activities.',
        source: 'Set out in Article 4 of our articles of association and approved by our shareholders.',
        concretTitre: 'What it means in practice',
        concret: [
          { cle: 'For the climate', texte: 'funding projects in France certified under the Label Bas-Carbone (afforestation, reforestation, low-carbon farming practices, hedgerows, orchards), whose emission reductions are measured using methods approved by the French Ministry for Ecological Transition.' },
          { cle: 'For local communities', texte: 'channelling corporate funding to French foresters and farmers, so that it pays for work on the ground.' },
          { cle: 'For nature', texte: 'selecting and monitoring projects also for what they bring to biodiversity, water and soils, beyond carbon.' },
        ],
        activiteTitre: 'How it guides our business',
        activiteIntro:
          'Our business is to connect companies that want to act for the climate with project developers who reduce or store carbon in France. Our purpose sets our rules of conduct:',
        regles: [
          'we track every tonne and every euro, all the way to the project;',
          'our executives take into account, in their decisions, the consequences for our employees, clients, project developers, shareholders and the environment, as our articles of association require.',
        ],
      },
      badge: 'About',
      heroTitleP1: 'For a',
      heroTitleHighlight: 'carbon contribution',
      heroSubtitle:
        'Stock CO2 supports companies and organisations in their CSR carbon offset journey with French projects labelled under the Label Bas-Carbone, the official scheme of the French Ministry for Ecological Transition.',
      title: 'Stock CO2, a carbon contribution rooted in French territories',
      subtitle:
        'Our mission: support companies towards a credible, transparent carbon contribution rooted in French territories.',
      missionTitle: 'Our mission',
      missionHeading: 'Making carbon contribution accessible, transparent and impactful',
      missionText:
        'Stock CO2 is dedicated to fighting climate change by supporting carbon offset projects. We work with foresters, farmers and institutions to develop and certify ecological projects under the Label Bas-Carbone criteria. Our approach relies on project diversity, secured carbon credits, financial transparency and project quality.',
      valuesTitle: 'Our values',
      valuesSubtitle: 'What guides us every day',
      values: [
        { icon: 'search', title: 'Transparency', description: 'Every data point, every metric, every euro is traced and verifiable. No greenwashing.' },
        { icon: 'leaf', title: 'Real impact', description: 'We measure impact across 5 ecosystem dimensions, not just carbon.' },
        { icon: 'handshake', title: 'No middleman', description: 'Direct relationship between contributing company and project operator. No mark-up.' },
        { icon: 'chart', title: 'Scientific rigour', description: 'Our methods are validated by the Ministry and audited by independent bodies.' },
      ],
      historyBadge: 'Our story',
      historyTitle: 'Key milestones',
      milestones: [
        { year: '2019', title: 'Three friends, one conviction', description: "Thomas Martal, Aubin Labergère and Guillaume Panthou found Stock CO2 to relocate carbon contribution in France. Accepted into the Urban Odyssey studio (ICADE) and the HEC incubator at Station F." },
        { year: '2020', title: 'First projects and first contributors', description: "First forestry projects labelled under the Label Bas-Carbone. First corporate contributors onboarded and development of a carbon calculator aligned with LBC forestry methodologies." },
        { year: '2021', title: 'Scaling up', description: "Significant acceleration with first major corporate clients (CAC 40). Stock CO2 grows in the French voluntary carbon market." },
        { year: '2022', title: 'Multi-sector operator and trust', description: "Stock CO2 becomes a multi-sector carbon operator (forestry, agriculture, livestock). Implementation of an environmental fiduciary trust at Caisse des Dépôts. Offsets the emissions of the Ministry for Ecological Transition. First internal carbon footprint published." },
        { year: '2023', title: 'Purpose, AFEF and insurance', description: "Corporate purpose written into the bylaws. Stock CO2 joins the Association Française des Eaux et Forêts (AFEF) and implements insurance coverage to secure its carbon projects." },
        { year: '2024', title: 'B Corp and institutional recognition', description: "Groupama insurance coverage. B Corp certification earned. Carbon contribution for Choose France, OFB and the IPCC." },
        { year: '2025', title: 'Nature+ Coalition', description: "Co-founding of the Nature+ Coalition alongside Reforest'Action, SE Advisory Services (formerly Ecoact, Schneider Electric) and Evolution Market to unite stakeholders in nature-based solutions." },
      ],
      certificationsBadge: 'Labels & Certifications',
      certificationsTitle: 'Labelled and certified commitments',
      certifications: [
        { icon: 'label', name: 'Label Bas-Carbone', desc: 'Official label of the French State' },
        { icon: 'bcorp', name: 'B Corp', desc: 'B Lab international certification' },
      ],
      partnersTitle: 'Our partner network',
      merciBadge: 'M.E.R.C.I method',
      merciTitle: 'Measure, Eliminate, Reduce, Compensate, Iterate',
      merciSubtitle:
        'Our integrated approach to support companies towards an ambitious and verifiable carbon strategy.',
      merciCta: 'Discover our impact in detail →',
      ctaBadge: 'Join us',
      ctaTitle: 'Commit your company to the transition',
      ctaSubtitle:
        'Join the companies that trust Stock CO2 with their carbon contribution.',
      ctaPrimary: 'Contact us',
      ctaSecondary: 'See our projects',
      teamTitle: 'A committed team',
      teamText:
        'Foresters, agronomists, CSR experts and developers: our team brings together the skills needed to identify, qualify and track reference French carbon projects.',
      visionText:
        "We believe in a positive carbon contribution, rooted in local territories and driven by concrete projects. Our ambition: to become the European reference for local carbon contribution. France first, then Europe — with the same standards of transparency, rigour and real impact.",
      originTitle: 'Our story',
      originText:
        "In 2019, Thomas Martal, Aubin Labergère and Guillaume Panthou — three friends concerned about the climate emergency — decided to create Stock CO2. Their observation: French companies lacked a local, credible and transparent carbon contribution offering. Rather than sending funds to the other side of the world, they chose to relocate the impact in France, working directly with local foresters and farmers.",
      bilanTitle: 'Carbon footprint: independence and rigour',
      bilanText:
        "Stock CO2 is a member of the Association Bilan Carbone and trained in carbon assessment methodology. However, since 2020, we have deliberately chosen not to carry out carbon assessments for our contributors. Why? To avoid being judge and jury. A provider that performs a company's carbon assessment and then sells them carbon credits faces a conflict of interest: they could be tempted to inflate results to justify a larger contribution volume. At Stock CO2, we prefer to clearly separate roles: diagnosis on one side, contribution on the other.",
      afefTitle: 'AFEF — Forestry & Water',
      afefText:
        "Stock CO2 is a member of the Association Française des Eaux et Forêts (AFEF). Co-founder Aubin Labergère serves as its Secretary General. This involvement reflects our deep commitment to French forestry and environmental issues.",
      coalitionTitle: 'Nature+ Coalition',
      coalitionText:
        "In 2025, Stock CO2 co-founded the Nature+ Coalition, an alliance bringing together key players in nature-based solutions. Alongside Reforest'Action, SE Advisory Services (formerly Ecoact, Schneider Electric) and Evolution Market, we work to structure and build credibility in the nature project market across France and Europe. Co-founder Guillaume Panthou serves as Vice-President of the coalition.",
    },
    faq: {
      badge: 'FAQ',
      title: 'Frequently asked questions',
      subtitle: 'Find answers to your questions about carbon contribution, our projects, pricing and CSRD compliance.',
      searchPlaceholder: 'Search a question…',
      noResults: 'No question matches your search.',
      categories: {
        general: 'General',
        tarifs: 'Pricing & Contribution',
        csrd: 'CSRD & Reporting',
        projets: 'Submit a Project',
        label: 'Label Bas-Carbone',
      },
      ctaBadge: 'Still have questions?',
      ctaTitle: 'Talk directly to our team',
      ctaSubtitle: 'We answer all your questions about carbon contribution and our projects.',
      ctaPrimary: 'Contact us',
      sections: [
        {
          title: 'General',
          items: [
            {
              q: 'What is carbon contribution?',
              a: 'Carbon contribution is an action that offsets greenhouse gas emissions by funding projects that reduce or capture CO₂. At Stock CO2, every project is labelled under the Label Bas-Carbone, run by the French Ministry for Ecological Transition.',
            },
            {
              q: 'What is the difference between offsetting and contribution?',
              a: 'Carbon contribution is a more responsible approach than mere offsetting. It is part of a broader process: measure, avoid, reduce, then contribute. Stock CO2 supports this end-to-end approach via the M.E.R.C.I method.',
            },
            {
              q: 'How are your carbon credits labelled?',
              a: 'All our projects are labelled under the Label Bas-Carbone, the official label of the French State. Each project follows a rigorous audit process and a five-year monitoring cycle by independent auditors.',
            },
            {
              q: 'Who is Stock CO2 for?',
              a: 'Stock CO2 is aimed at companies and organisations looking to commit to a responsible and verifiable carbon contribution as part of their CSR strategy.',
            },
            {
              q: 'What types of projects do you offer?',
              a: 'We offer reforestation, afforestation, large-crop (Carbon Agri), livestock and building projects. They are all located in France and labelled under the Label Bas-Carbone.',
            },
            {
              q: 'What are co-benefits?',
              a: 'Co-benefits are the additional positive impacts of a carbon project beyond CO₂ storage: biodiversity, water preservation, soil protection and socio-economic effects.',
            },
          ],
        },
        {
          title: 'Pricing & Contribution',
          items: [
            {
              q: 'Why does the price vary between €30-50?',
              a: 'The price depends on project type (forest vs agricultural), geographical location, measured co-benefits (biodiversity, water, soil, socio-economic impact), and specific site conditions. Agricultural projects can reach €50/tCO₂ thanks to significant agronomic co-benefits.',
            },
            {
              q: 'What\'s included in the price of a carbon credit?',
              a: 'The price includes: (1) project implementation costs (35%), (2) risk insurance (15%), (3) trust fees guaranteeing longevity for up to 9 years (forest) and 5 years (agriculture) (20%), (4) Stock CO2 operator fees for management and sales (20%), (5) Label Bas-Carbone certification by independent audits (10%). No hidden costs.',
            },
            {
              q: 'Why do prices increase every year?',
              a: 'Prices increase 5-10% annually since 2019 due to: material inflation (plants, equipment), labor cost increases, and rising environmental and insurance standards. This trend reflects real implementation costs. Stock CO2 adjusts pricing annually to maintain project quality.',
            },
            {
              q: 'Is Label Bas-Carbone cheaper than Verra or Gold Standard?',
              a: 'Yes, Label Bas-Carbone pricing is 10-20% more competitive than international standards. Why? Because there are no unauthorized intermediaries. Stock CO2 operates directly, reducing sales costs. You benefit from greater transparency and better value for your carbon investment.',
            },
            {
              q: 'How is the carbon credit price calculated?',
              a: 'Stock CO2 uses a full-cost model integrating: on-site expenses (implementation, insurance, trust), operational fees, and certification costs. Each project is independently audited. Prices are set annually based on real costs, market demand, and France\'s climate targets.',
            },
            {
              q: 'Are there any hidden fees?',
              a: 'No. Our pricing is completely transparent. The displayed price includes all costs: implementation, insurance, trust, operations, certification. No hidden charges. You pay exactly what\'s announced with complete traceability of how your contribution is used.',
            },
            {
              q: 'Is there a discount for large volumes?',
              a: 'Yes. For purchases of 1000+ tCO₂, we offer volume discounts and customized support conditions. Contact our team to discuss your project and receive a quote tailored to your volume.',
            },
            {
              q: 'How long to receive my carbon credits?',
              a: 'Once your order is confirmed, project selection and integration take 2-4 weeks. Credits are generated according to Label Bas-Carbone\'s release schedule. Forest projects average 6 months. Agricultural projects average 3-4 months.',
            },
            {
              q: 'How does the contribution process work?',
              a: 'The process is simple: we discuss your needs, you select one or several projects, we set up a contribution contract and you receive a Label Bas-Carbone carbon contribution statement.',
            },
            {
              q: 'Can I use my contribution in my CSR reporting?',
              a: 'Yes, your Label Bas-Carbone approved contribution can be reported in your CSR reporting, carbon footprint and extra-financial communications. We provide every document you need.',
            },
          ],
        },
        {
          title: 'CSRD & Reporting',
          items: [
            {
              q: 'Can carbon credits offset my emissions in CSRD reporting?',
              a: 'No. CSRD requires raw reporting (without offsetting). Purchased carbon credits must be separately declared in ESRS E1-7: amount in tCO₂, certification standard (Label Bas-Carbone), and % reduction vs. sequestration. Stock CO2 provides this data for compliant reporting.',
            },
            {
              q: 'How do I declare my carbon contribution in my CSRD report?',
              a: 'Via the ESRS E1-7 section of your disclosure. You declare: tons of CO₂ financed, certification standard (Label Bas-Carbone), reduction/sequestration breakdown. Stock CO2 structures this data to ease your reporting and provides complete audit traceability.',
            },
            {
              q: 'Is Label Bas-Carbone recognized for CSRD?',
              a: 'Yes. Label Bas-Carbone is a French standard run by the Ministry for Ecological Transition. It is explicitly recognized in ESRS standards as a quality standard for declaring purchased carbon credits.',
            },
            {
              q: 'Does Stock CO2 replace a carbon footprint assessment?',
              a: 'No, Stock CO2 is complementary. A carbon footprint assessment measures your emissions (step 1: identify and reduce). Stock CO2 finances sequestration projects for your residual emissions (step 2: contribute). Both are essential for CSRD: first measure, then act.',
            },
            {
              q: 'What is double materiality (CSRD)?',
              a: 'Double materiality assesses two directions: (1) your company\'s impact on the environment (e.g., your carbon emissions), and (2) the environment\'s impact on your company (e.g., physical climate risks). Label Bas-Carbone projects cover both, justifying your CSRD engagement.',
            },
            {
              q: 'When must my company comply with CSRD?',
              a: '2025: Large companies already subject to NFRD (>500 employees). 2026: Other large companies (1000+ employees, revenue >€450M). 2027-2028: Listed SMEs (with deferral option for 2 years). Stock CO2 helps you prepare your reporting starting now.',
            },
            {
              q: 'Can a single Label Bas-Carbone project cover multiple ESRS standards?',
              a: 'Yes. Each Label Bas-Carbone project is evaluated on 5 co-benefit dimensions: climate (E1), biodiversity (E4), water (E3), soil (E5), and socio-economic impact (S3). A single project can thus contribute to multiple ESRS standards simultaneously.',
            },
            {
              q: 'What are the penalties for CSRD non-compliance?',
              a: 'Non-compliant companies face regulatory penalties, administrative fines, and reputational damage (loss of investor, customer, and partner trust). CSRD compliance is becoming progressively mandatory. Stock CO2 helps you anticipate it.',
            },
          ],
        },
        {
          title: 'Submit a Project',
          items: [
            {
              q: 'Who is eligible for Label Bas-Carbone certification?',
              a: 'Any owner or manager of forest or agricultural land can submit a project. Feasibility will be assessed during the diagnostic phase: minimum size, carbon potential, location, and sustainability criteria compliance.',
            },
            {
              q: 'What is the cost to propose and certify my project?',
              a: 'Stock CO2 takes a commission on monetized carbon credits. There is no upfront cost to submit your project or conduct the assessment. Certification costs are shared equitably between Stock CO2 and the project holder.',
            },
            {
              q: 'How long does it take to obtain Label Bas-Carbone certification?',
              a: 'Typically 6-12 months depending on project complexity and any necessary adjustments. The assessment phase takes 2-4 weeks, and project setup 3-6 months.',
            },
            {
              q: 'What obligations must I meet once labelled?',
              a: 'You must maintain the practices described in your management plan for the project\'s duration (typically 30-50 years for forest projects). Stock CO2 ensures annual monitoring and reporting to guarantee compliance.',
            },
          ],
        },
        {
          title: 'Label Bas-Carbone',
          items: [
            {
              q: 'What is the Label Bas-Carbone?',
              a: 'The Label Bas-Carbone is the official label of the French State, created in 2019 by the Ministry for Ecological Transition. It labels projects that reduce or sequester GHG emissions on French soil.',
            },
            {
              q: 'What is B Corp certification?',
              a: 'B Corp is an international certification issued by B Lab that confirms Stock CO2 meets the highest standards for social and environmental impact, governance and transparency.',
            },
            {
              q: 'How do I choose the right project for my company?',
              a: 'Our experts help you choose the project best suited to your industry, location and CSR goals.',
            },
          ],
        },
      ],
    },
    entreprises: {
      badge: 'Companies',
      title: 'A credible carbon strategy for your company',
      subtitle:
        'Labelled French projects, clear reporting and support across the value chain.',
      offerTitle: 'Our business offer',
      stepsTitle: 'How it works',
      steps: [
        { num: '1', title: 'Assessment', text: 'Analysis of your footprint and CSR targets.' },
        { num: '2', title: 'Project selection', text: 'Choose projects suited to your sector and values.' },
        { num: '3', title: 'Contribution', text: 'Secure transaction and detailed reporting.' },
        { num: '4', title: 'Reporting', text: 'CSR communication and CSRD-compatible reporting.' },
      ],
      platformTitle: 'Your tracking dashboard',
      platformSubtitle: 'Track your contributions in real time with a platform dedicated to engaged companies.',
      platformDescription: 'Discover the tracking dashboard and its features with our team.',
      platformFeatures: [
        {
          icon: 'clock',
          title: 'Real-time tracking',
          description: 'View your projects and carbon credits status at a glance. Personalized dashboards and instant updates.',
        },
        {
          icon: 'document',
          title: 'Automated reporting',
          description: 'CSRD-compatible exports for your ESG reporting. Documents ready to integrate into your sustainability report.',
        },
        {
          icon: 'chart',
          title: 'Dashboard',
          description: 'Impact metrics across 5 ecosystem dimensions. See your contribution to climate, biodiversity, water, soil and socio-economic value.',
        },
      ],
      platformCta: 'Request a demo',
    },
    forestiers: {
      badge: 'Forestry projects',
      title: 'Labelled reforestation and afforestation',
      subtitle:
        'Our forestry projects restore ecosystems, sequester carbon and strengthen local biodiversity.',
      intro:
        'Stock CO2 supports reforestation and afforestation projects labelled under the Label Bas-Carbone across France. Each project is selected for its carbon sequestration potential and its co-benefits on biodiversity, water, soil and the local economy.',
      statsLabels: {
        co2: 'tCO₂ stored (forestry)',
        projects: 'Forestry projects',
        hectares: 'Hectares involved',
        trees: 'Seedlings planned in project files',
      },
      featuresBadge: 'Our strengths',
      featuresTitle: 'What makes the difference',
      features: [
        {
          icon: 'pine',
          title: 'Diversified species',
          description:
            'Planting of varied species (oak, linden, hornbeam, wild cherry…) to strengthen forest resilience to climate change.',
        },
        {
          icon: 'butterfly',
          title: 'Ecological corridors',
          description:
            'Creation of corridors linking forest massifs to encourage wildlife movement and pollination.',
        },
        {
          icon: 'droplets',
          title: 'Water protection',
          description:
            'Forests naturally filter water and protect groundwater. Our projects are often located in catchment areas.',
        },
        {
          icon: 'clipboard',
          title: 'Five-yearly monitoring',
          description:
            'Each project is monitored every 5 years by independent auditors to verify the actual sequestration.',
        },
      ],
      seeAllProjects: 'View all projects →',
      cta: 'Propose a forestry project',
    },
    agricoles: {
      badge: 'Agricultural projects',
      title: 'Labelled low-carbon agriculture',
      subtitle:
        'Our agricultural projects reduce emissions, enrich soils and support French farmers.',
      intro:
        'Stock CO2 supports low-carbon agricultural projects via the Carbon Agri method, labelled under the Label Bas-Carbone. These projects optimise crop and livestock practices to reduce greenhouse gas emissions while improving soil health.',
      statsLabels: {
        co2: 'tCO₂ reduced (agricultural)',
        projects: 'Agricultural projects',
        hectares: 'Hectares involved',
        regions: 'Regions covered',
      },
      featuresBadge: 'Our strengths',
      featuresTitle: 'What makes the difference',
      features: [
        {
          icon: 'wheat',
          title: 'Regenerative practices',
          description:
            'Permanent soil cover, crop rotation, reduction of chemical inputs to restore natural fertility.',
        },
        {
          icon: 'cow',
          title: 'Low-carbon livestock',
          description:
            'Optimisation of animal feed, effluent management and grazing valorisation to reduce emissions.',
        },
        {
          icon: 'sprout',
          title: 'Soil sequestration',
          description:
            'Regenerative agricultural practices increase soil organic carbon, creating a durable carbon sink.',
        },
        {
          icon: 'handshake',
          title: 'Support for farmers',
          description:
            'Carbon credits generate additional income for farmers who commit to the transition.',
        },
      ],
      seeAllProjects: 'View all projects →',
      cta: 'Propose an agricultural project',
    },
    sectoralCta: {
      badge: 'Take action',
      title: 'Contribute to the transition with our projects',
      subtitle: 'Choose the project that fits your CSR strategy and values.',
      primary: 'Talk to an expert',
      secondary: 'Discover our projects',
    },
    entreprisesPage: {
      badge: 'Companies',
      title: 'They contribute with Stock CO2',
      subtitle: 'Discover the companies and organisations that trust us.',
      sectorLabel: 'Sector',
      seeMore: 'View partnership →',
      backToList: '← All companies',
      engagementTitle: 'Their commitment',
      ctaBadge: 'Inspired?',
      ctaTitle: 'Engage your company like them',
      ctaSubtitle: 'Join the companies supporting carbon offset projects with Stock CO2.',
      ctaPrimary: 'Contact us',
      notFound: 'Company not found',
    },
    impactPage: {
      badge: 'Impact',
      title: 'Our 5-dimension assessment method',
      subtitle:
        'Beyond carbon, we assess every project on audited and verified co-benefits.',
      heroBadge: 'Our impact',
      heroTitleP1: 'Measured impact across',
      heroTitleHighlight: '5 dimensions',
      heroTitleP2: 'of the ecosystem',
      heroSubtitle:
        'Beyond carbon, every Stock CO2 project generates verified co-benefits across the whole ecosystem: climate, biodiversity, water, soil and socio-economic.',
      statsLabels: {
        projects: 'Labelled projects',
        co2: 'tCO₂ sequestered',
        hectares: 'Hectares involved',
        regions: 'French regions',
      },
      dimensionsBadge: '5 dimensions',
      dimensionsTitle: 'A complete ecosystem impact',
      dimensionsSubtitle:
        'Each dimension is qualified using the official Label Bas-Carbone documentation and the information provided by project operators.',
      dimensions: {
        climat: {
          title: 'Climate',
          description:
            'Amount of CO₂ sequestered or avoided, measured using the official Label Bas-Carbone methods.',
          facts: [
            'Methods recognised by the French Ministry of Ecological Transition',
            'Five-yearly audit of every project by an independent body',
            'Reductions and removals registered in the public Label Bas-Carbone registry',
          ],
        },
        biodiversite: {
          title: 'Biodiversity',
          description:
            'Species diversity, habitat quality, presence of indicator species and ecological continuity.',
          facts: [
            'Diversification of forest species and stand structures',
            'Creation or preservation of habitats: hedges, edges, ponds',
            'Recommendations from the official Label Bas-Carbone methods',
          ],
        },
        eau: {
          title: 'Water',
          description:
            'Water quality, hydrological regulation, wetland preservation and erosion management.',
          facts: [
            'Reduction of runoff and water erosion',
            'Preservation of wetlands and living soils',
            'Practices supporting infiltration and water quality',
          ],
        },
        sols: {
          title: 'Soil',
          description:
            'Soil carbon storage, biological activity, erosion resistance and long-term fertility.',
          facts: [
            'Cover crops and reduced tillage',
            'Organic inputs and preservation of organic matter',
            'Monitoring of soil quality indicators',
          ],
        },
        socioEconomique: {
          title: 'Socio-economic',
          description:
            'Field work mobilised, rural territory valorisation, educational and generational transmission.',
          facts: [
            'Direct relationship with the foresters and farmers operating projects',
            'Activity sustained in local supply chains (nurseries, forestry contractors)',
            'Valorisation of the natural heritage of territories',
          ],
        },
      },
      methodologyBadge: 'M.E.R.C.I method',
      methodologyTitle: 'A 5-step methodology',
      methodologyIntro:
        'Measure, Avoid, Reduce, Contribute, Iterate — our integrated approach for an ambitious and credible carbon strategy.',
      methodologySteps: [
        {
          number: 'M',
          title: 'Measure',
          icon: 'chart',
          text: 'Carry out a complete carbon assessment of your organisation using a certified methodology. Identify your emission sources.',
        },
        {
          number: 'A',
          title: 'Avoid',
          icon: 'ban',
          text: 'Implement concrete actions to avoid emissions at their source. Prioritise sobriety and eco-design.',
        },
        {
          number: 'R',
          title: 'Reduce',
          icon: 'trending-down',
          text: 'Optimise your processes to significantly reduce your unavoidable emissions. Set measurable targets.',
        },
        {
          number: 'C',
          title: 'Contribute',
          icon: 'sprout',
          text: 'Offset your residual emissions by funding projects labelled under the Label Bas-Carbone with ecosystem impact.',
        },
        {
          number: 'I',
          title: 'Iterate',
          icon: 'refresh',
          text: 'Monitor, audit and continuously improve your approach. Every year, progress towards an ever smaller footprint.',
        },
      ],
      certificationsBadge: 'Certifications & Labels',
      certificationsTitle: 'Labelled and certified commitments',
      certificationsSubtitle:
        'Our approach is validated by independent bodies and recognised nationally and internationally.',
      certificationsItems: [
        {
          name: 'Label Bas-Carbone',
          issuer: 'French Ministry of Ecological Transition',
          icon: 'label',
          description:
            'Official certification of the French state guaranteeing the additionality and quality of carbon projects.',
        },
        {
          name: 'B Corp',
          issuer: 'B Lab',
          icon: 'bcorp',
          description:
            'International certification attesting that Stock CO2 meets demanding standards in social and environmental impact.',
        },
      ],
      partnersNetworkTitle: 'Our partner network',
      ctaBadge: 'Contribute to the impact',
      ctaTitle: 'Measure your footprint, choose your impact',
      ctaSubtitle:
        'Every contribution funds projects with measurable and verifiable ecosystem impact.',
      ctaPrimary: 'Start your contribution',
      ctaSecondary: 'View our projects',
    },
    engagements: {
      badge: 'Our commitments',
      title: 'Six commitments for a credible carbon contribution',
      subtitle:
        'Stock CO2 follows a strict ethical framework to guarantee the real impact of every project.',
      heroBadge: 'Our commitments',
      heroTitle: 'Committed to the climate',
      heroSubtitle:
        'Our commitments go beyond words. Certifications, publications and a rigorous methodology guide our daily action.',
      items: [
        {
          icon: 'bcorp',
          title: 'B Corp Certified',
          description:
            'Stock CO2 has been awarded the B Corp certification by B Lab, attesting that we meet demanding standards for social, environmental, governance and transparency impact.',
        },
        {
          icon: 'chart',
          title: 'A carbon assessment published every year',
          description:
            'We have published our own carbon assessment every year since the 2025 financial year (2023 and 2025 assessments completed). We measure, reduce and offset our residual emissions with the same rigour as the one we offer our clients.',
        },
        {
          icon: 'refresh',
          title: 'M.E.R.C.I method',
          description:
            'Measure, Avoid, Reduce, Contribute, Iterate — our 5-step methodology guides every company towards an ambitious and verifiable carbon strategy.',
        },
        {
          icon: 'document',
          title: 'Purpose written into our bylaws',
          description:
            'Since 2023, our purpose has been written into the company bylaws, ensuring that our environmental mission guides every strategic decision.',
        },
      ],
      positionBadge: 'Position papers',
      positionTitle: 'Our positions',
      positionSubtitle:
        'Find our publications and analyses on the challenges of carbon contribution.',
      positionPapers: [
        { title: 'M.E.R.C.I methodology', theme: 'csr' },
        { title: 'Responsible carbon contribution', theme: 'carbon' },
        { title: 'Biodiversity and forestry projects', theme: 'biodiversity' },
        { title: 'CSRD and extra-financial reporting', theme: 'regulation' },
      ],
      readMore: 'Read →',
      ctaBadge: 'Join the movement',
      ctaTitle: 'Engage your company for the climate',
      ctaPrimary: 'Contact us',
      ctaSecondary: 'Discover our impact',
    },
    blog: {
      heroBadge: 'Blog',
      heroTitle: 'Carbon and CSR insights',
      heroSubtitle:
        'Analyses, practical guides and feedback to inform your carbon contribution journey.',
      categoryAll: 'All',
      categories: {
        carbone: 'Carbon',
        biodiversite: 'Biodiversity',
        reglementation: 'Regulation',
        rse: 'CSR',
        projets: 'Projects',
        innovation: 'Innovation',
        actualites: 'News',
        guides: 'Practical Guides',
        temoignages: 'Testimonials',
        impact: 'Impact & Results',
        dossiers: 'Expert Reports',
      },
      readTimeUnit: 'min',
      backToBlog: '← Back to blog',
      allArticles: '← All articles',
      notFound: 'Article not found',
      ctaBadge: 'Want to learn more?',
      ctaTitle: 'Talk to our carbon experts',
      ctaSubtitle:
        'We support you in defining and implementing your carbon contribution strategy.',
      ctaPrimary: 'Contact us',
    },
    footer: {
      tagline: 'Carbon contribution with ecosystem impact, through labelled French projects.',
      newsletter: 'Stay informed about our projects',
      newsletterSub: 'Receive our news and new carbon contribution opportunities.',
      emailPlaceholder: 'Your professional email',
      subscribe: 'Subscribe',
      subscribed: 'Thanks, you are subscribed!',
      solutions: 'Solutions',
      solutionsLinks: {
        entreprises: 'Companies',
        forestiers: 'Foresters',
        agricoles: 'Farmers',
        impact: 'Our impact',
        pricing: 'Pricing',
        howItWorks: 'How it works',
        reportingCsrd: 'CSRD Reporting',
      },
      company: 'Company',
      companyLinks: {
        about: 'About',
        engagements: 'Commitments',
        caseStudies: 'Case studies',
        blog: 'Blog',
        contact: 'Contact',
      },
      legal: 'Legal',
      legalLinks: {
        mentions: 'Legal notices',
        confidentialite: 'Privacy policy',
        cgu: 'Terms of service',
        cookies: 'Cookies',
      },
      rights: 'All rights reserved.',
      certified: 'B Corp Certified',
      label: 'Label Bas-Carbone',
    },
    notFound: {
      title: 'Page not found',
      subtitle:
        "This page seems to have lost its way in the forest. Don't worry, our carbon projects are still here!",
      backHome: 'Back to home',
      seeProjects: 'See our projects',
    },
    common: {
      backTo: '← Back',
      readMore: 'Read →',
      learnMore: 'Learn more',
      loading: 'Loading…',
      error: 'An error occurred.',
      yes: 'Yes',
      no: 'No',
      close: 'Close',
      open: 'Open',
    },
  },
} as const

export type Dictionary = (typeof dictionaries)['fr']

export function getDictionary(locale: Locale): Dictionary {
  return (dictionaries[locale] || dictionaries.fr) as Dictionary
}
